import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {ChevronDown, ChevronUp, ImageIcon, Loader2, Plus, Power, Search, Trash2, Upload, X} from 'lucide-react';
import {Dialog} from '@headlessui/react';
import {toast} from 'react-toastify';
import clsx from 'clsx';
import Card from '@/shared/components/Card';
import Button from '@/shared/components/Button';
import {AdminService} from '@/modules/admin/services/adminService';
import {resolveMediaUrl} from '@/shared/utils/mediaUrl';
import {
    type OrganizationProvider,
    type OrganizationProviderConsoleResponse,
    type OrganizationProviderConsoleUpdateRequest,
    type OrganizationSearchResult,
    type ProviderConsoleModuleInput,
    type OrganizationProviderModulesInput,
    type OrganizationLoginUser,
    type ProviderLoginUser,
    type ProviderRoleRow,
} from '@/shared/types/api';
import {
    ProviderConsoleModules,
    ProviderConsoleModuleIds,
    ProviderConsoleModuleLabels,
    type ProviderConsoleModuleKey,
} from '@mld/shared/constants/providerConsoleAccess';
import {
    PatientDashboardModuleIds,
    PatientDashboardModuleKeyById,
    PatientDashboardModuleLabels,
    PATIENT_DASHBOARD_MODULE_KEYS,
    type PatientDashboardModuleId,
    type PatientDashboardModuleKey,
} from '@mld/shared/constants/dashboardModuleAccess';
import {
    isOrganizationRole,
    isProviderRole,
    isSuperAdministratorRole,
    isPatientRole,
} from '@mld/shared/constants/userRoles';
import {shouldSuppressPatientEmails, type PatientEmailFeatures} from '@mld/shared/organizations/patientEmails';
import {getBaseDomain} from '@/shared/utils/domain';
import {
    BRAND_TOKENS,
    effectiveDefaultHex,
    parseCustomCss,
    serializeCustomCss,
} from '@/modules/admin/utils/customCssTokens';

import {useProviderConsoleSwitch} from '@/modules/provider-console/hooks/useProviderConsoleSwitch';

const MODULE_KEYS = Object.values(ProviderConsoleModules) as ProviderConsoleModuleKey[];
const SUBDOMAIN_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const PROVIDER_PAGE_SIZE = 15;
const INPUT_BASE_CLASS = 'w-full rounded-md border px-3 py-2 text-sm focus:ring-2';
const INPUT_DEFAULT_CLASS = `${INPUT_BASE_CLASS} border-gray-300 focus:border-transparent focus:ring-mld-primary`;
const INPUT_ERROR_CLASS = `${INPUT_BASE_CLASS} border-mld-danger focus:border-mld-danger focus:ring-rose-200`;

const MODULE_LABELS: Record<ProviderConsoleModuleKey, string> = ProviderConsoleModuleLabels;

const PATIENT_MODULE_KEYS = PATIENT_DASHBOARD_MODULE_KEYS;
const PATIENT_MODULE_LABELS: Record<PatientDashboardModuleKey, string> = PatientDashboardModuleLabels;

type ModuleMap = Record<ProviderConsoleModuleKey, boolean>;
/** Org level is complete (default-on fill). */
type PatientModuleMap = Record<PatientDashboardModuleKey, boolean>;
/**
 * Provider level is SPARSE: `undefined` means "no explicit row" and renders
 * as the default (on). Only explicit entries are sent on save, so untouched
 * cells never materialize provider_modules rows.
 */
type PatientProviderModuleMap = Partial<Record<PatientDashboardModuleKey, boolean>>;
type UserFormState = {first_name: string; last_name: string; email: string; phone: string};
type ProviderUserPayload = UserFormState & {role_ids?: number[]};
type EditUserState = {id: number; form: UserFormState};
type EditProviderUserState = {id: number; providerId: number; form: UserFormState; role_id: number | null};
type FormFieldErrors = Partial<Record<keyof UserFormState, boolean>>;
type ProviderSortKey =
    | 'name'
    | 'is_active'
    | ProviderConsoleModuleKey
    | PatientDashboardModuleKey;

type OrgDetailTab = 'modules' | 'providers' | 'subdomain' | 'logins' | 'all';

interface OrganizationProviderConsoleProps {
    selectedOrg?: OrganizationSearchResult | null;
    onSelectOrg?: (org: OrganizationSearchResult) => void;
    showSearch?: boolean;
    activeTab?: OrgDetailTab;
    showActionBar?: boolean;
    orgFilter?: string;
}

function buildModuleMap(source?: ProviderConsoleModuleInput[] | null, defaultValue = false): ModuleMap {
    const idMap = new Map<number, boolean>();
    source?.forEach((row) => {
        idMap.set(row.module_id, Boolean(row.enabled));
    });

    return MODULE_KEYS.reduce((acc, key) => {
        const moduleId = ProviderConsoleModuleIds[key];
        const raw = idMap.has(moduleId) ? idMap.get(moduleId) : undefined;
        acc[key] = raw == null ? defaultValue : Boolean(raw);
        return acc;
    }, {} as ModuleMap);
}

function moduleMapToArray(map: ModuleMap): ProviderConsoleModuleInput[] {
    return MODULE_KEYS.map((key) => ({
        module_id: ProviderConsoleModuleIds[key],
        enabled: Boolean(map[key]),
    }));
}

function buildPatientOrgModuleMap(source?: ProviderConsoleModuleInput[] | null): PatientModuleMap {
    const idMap = new Map<number, boolean>();
    source?.forEach((row) => {
        idMap.set(row.module_id, Boolean(row.enabled));
    });

    return PATIENT_MODULE_KEYS.reduce((acc, key) => {
        const raw = idMap.get(PatientDashboardModuleIds[key]);
        // Patient modules are default-ON (no row = enabled).
        acc[key] = raw == null ? true : Boolean(raw);
        return acc;
    }, {} as PatientModuleMap);
}

function buildPatientProviderModuleMap(source?: ProviderConsoleModuleInput[] | null): PatientProviderModuleMap {
    const map: PatientProviderModuleMap = {};
    source?.forEach((row) => {
        const key = PatientDashboardModuleKeyById[row.module_id as PatientDashboardModuleId];
        if (key) {
            map[key] = Boolean(row.enabled);
        }
    });
    return map;
}

function patientOrgModuleMapToArray(map: PatientModuleMap): ProviderConsoleModuleInput[] {
    return PATIENT_MODULE_KEYS.map((key) => ({
        module_id: PatientDashboardModuleIds[key],
        enabled: Boolean(map[key]),
    }));
}

function patientProviderModuleMapToArray(map: PatientProviderModuleMap): ProviderConsoleModuleInput[] {
    return PATIENT_MODULE_KEYS.filter((key) => map[key] !== undefined).map((key) => ({
        module_id: PatientDashboardModuleIds[key],
        enabled: Boolean(map[key]),
    }));
}

function formatBool(value: boolean | null | undefined): string {
    if (value == null) return '—';
    return value ? 'Yes' : 'No';
}

function parseContactName(name?: string | null): {first_name: string; last_name: string} {
    if (!name) return {first_name: '', last_name: ''};
    const trimmed = name.trim();
    if (!trimmed) return {first_name: '', last_name: ''};
    if (trimmed.includes(',')) {
        const [last, first] = trimmed.split(',').map((part) => part.trim());
        return {first_name: first || '', last_name: last || ''};
    }
    const parts = trimmed.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return {first_name: parts[0], last_name: ''};
    return {first_name: parts[0], last_name: parts.slice(1).join(' ')};
}

function formatPhoneInput(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function normalizePhoneForSave(value: string): string {
    return value.replace(/\D/g, '');
}

function formatPhoneFromRaw(value?: string | null): string {
    if (!value) return '';
    return formatPhoneInput(value);
}

function getErrorMessage(error: unknown, fallback: string): string {
    const err = error as {response?: {data?: {error?: string; message?: string; errors?: unknown[]}}; message?: string} | undefined;
    const data = err?.response?.data;
    if (typeof data === 'string') return data;
    const directMessage = data?.error || data?.message;
    if (directMessage) return directMessage;
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const messages = data.errors
            .map((entry) => {
                const e = entry as {message?: string; error?: string} | string;
                if (typeof e === 'string') return e;
                return e?.message || e?.error;
            })
            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
        if (messages.length > 0) return messages.join(', ');
    }
    if (typeof err?.message === 'string' && err.message.trim().length > 0) return err.message;
    return fallback;
}

function getDefaultProviderRoleId(roles: ProviderRoleRow[] | undefined): number | null {
    if (!roles || roles.length === 0) return null;
    const preferred = roles.find((role) => role.is_default);
    return preferred?.id ?? roles[0]?.id ?? null;
}

export const OrganizationProviderConsole: React.FC<OrganizationProviderConsoleProps> = ({
    selectedOrg: selectedOrgOverride,
    onSelectOrg,
    showSearch = true,
    activeTab = 'all',
    showActionBar = false,
    orgFilter,
}) => {
    const queryClient = useQueryClient();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<OrganizationSearchResult[]>([]);
    const [selectedOrg, setSelectedOrg] = useState<OrganizationSearchResult | null>(null);
    const [orgModules, setOrgModules] = useState<ModuleMap | null>(null);
    const [providers, setProviders] = useState<OrganizationProvider[]>([]);
    const [providerModules, setProviderModules] = useState<Record<number, ModuleMap>>({});
    const orgModulesRef = useRef<ModuleMap | null>(null);
    const providerModulesRef = useRef<Record<number, ModuleMap>>({});
    const [patientOrgModules, setPatientOrgModules] = useState<PatientModuleMap | null>(null);
    const [patientProviderModules, setPatientProviderModules] = useState<Record<number, PatientProviderModuleMap>>({});
    const patientOrgModulesRef = useRef<PatientModuleMap | null>(null);
    const patientProviderModulesRef = useRef<Record<number, PatientProviderModuleMap>>({});
    // null = no explicit choice; the org inherits from OHD mode.
    const [patientEmailsToggle, setPatientEmailsToggle] = useState<boolean | null>(null);
    // "Login via MLD": OHD-imported patients sign in here, so they get MLD's
    // login emails (verification, password reset, 2FA). Off by default.
    const [loginViaMld, setLoginViaMld] = useState(false);
    const [providerRolesByProvider, setProviderRolesByProvider] = useState<Record<number, ProviderRoleRow[]>>({});
    const [providerUserRoleMap, setProviderUserRoleMap] = useState<Record<number, number[]>>({});
    const [organizationUsers, setOrganizationUsers] = useState<OrganizationLoginUser[]>([]);
    const [providerUsers, setProviderUsers] = useState<ProviderLoginUser[]>([]);
    const [subdomain, setSubdomain] = useState('');
    const [subdomainOriginal, setSubdomainOriginal] = useState('');
    const [subdomainMessage, setSubdomainMessage] = useState<{type: 'success' | 'error' | ''; text: string}>(
        {type: '', text: ''}
    );
    const [providerGroupsOpen, setProviderGroupsOpen] = useState<Record<number, boolean>>({});
    const [orgAddOpen, setOrgAddOpen] = useState(false);
    const [orgUserForm, setOrgUserForm] = useState<UserFormState>({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
    });
    const [orgUserFieldErrors, setOrgUserFieldErrors] = useState<FormFieldErrors>({});
    const [orgUserEdit, setOrgUserEdit] = useState<EditUserState | null>(null);
    const [orgUserEditFieldErrors, setOrgUserEditFieldErrors] = useState<FormFieldErrors>({});
    const [providerUserForms, setProviderUserForms] = useState<Record<number, UserFormState>>({});
    const [providerAddOpen, setProviderAddOpen] = useState<Record<number, boolean>>({});
    const [providerUserRoleSelections, setProviderUserRoleSelections] = useState<Record<number, number | null>>({});
    const [providerUserEdit, setProviderUserEdit] = useState<EditProviderUserState | null>(null);
    const [providerUserFieldErrors, setProviderUserFieldErrors] = useState<Record<number, FormFieldErrors>>({});
    const [orgUserMessage, setOrgUserMessage] = useState<{type: 'success' | 'error' | ''; text: string}>({
        type: '',
        text: '',
    });
    const [orgUserEditMessage, setOrgUserEditMessage] = useState<{type: 'success' | 'error' | ''; text: string}>({
        type: '',
        text: '',
    });
    const [providerUserMessages, setProviderUserMessages] = useState<Record<number, {type: 'success' | 'error' | ''; text: string}>>({});
    const [providerUserEditMessage, setProviderUserEditMessage] = useState<{type: 'success' | 'error' | ''; text: string}>({
        type: '',
        text: '',
    });
    const [providerNameEditing, setProviderNameEditing] = useState<Record<number, boolean>>({});
    const [providerNameDrafts, setProviderNameDrafts] = useState<Record<number, string>>({});
    const [orgNameEditing, setOrgNameEditing] = useState(false);
    const [orgNameDraft, setOrgNameDraft] = useState('');
    const [orgNameSaving, setOrgNameSaving] = useState(false);
    const [providerNameSaving, setProviderNameSaving] = useState<number | null>(null);
    const [addProviderOpen, setAddProviderOpen] = useState(false);
    const [addProviderName, setAddProviderName] = useState('');
    const [addProviderNpi, setAddProviderNpi] = useState('');
    const [addProviderMessage, setAddProviderMessage] = useState<{type: 'success' | 'error' | ''; text: string}>({type: '', text: ''});
    const [deleteProviderOpen, setDeleteProviderOpen] = useState(false);
    const [deleteProviderId, setDeleteProviderId] = useState<number | null>(null);
    const [deleteProviderName, setDeleteProviderName] = useState('');
    const [deleteProviderTarget, setDeleteProviderTarget] = useState<number | null>(null);
    const [deleteProviderMessage, setDeleteProviderMessage] = useState<{type: 'success' | 'error' | ''; text: string}>({type: '', text: ''});
    const [statusMessage, setStatusMessage] = useState<{type: 'success' | 'error' | ''; text: string}>(
        {type: '', text: ''}
    );
    const [logoUrl, setLogoUrl] = useState('');
    const [logoUrlInput, setLogoUrlInput] = useState('');
    const [logoMessage, setLogoMessage] = useState<{type: 'success' | 'error' | ''; text: string}>({
        type: '',
        text: '',
    });
    const [logoModalOpen, setLogoModalOpen] = useState(false);
    const [logoModalFile, setLogoModalFile] = useState<File | null>(null);
    const [logoModalPreview, setLogoModalPreview] = useState<string | null>(null);
    const [logoModalDragOver, setLogoModalDragOver] = useState(false);
    // White-labeling: per-org CSS that overrides --mld-* tokens on bootstrap.
    // The form is split into a structured token map (color pickers) and a
    // free-form "extras" textarea; serializeCustomCss roundtrips them to a
    // single stored string.  See STYLE_GUIDE.md §2.
    const [customCssTokens, setCustomCssTokens] = useState<Record<string, string>>({});
    const [customCssExtras, setCustomCssExtras] = useState('');
    const [customCssOriginal, setCustomCssOriginal] = useState('');
    const [customCssMessage, setCustomCssMessage] = useState<{type: 'success' | 'error' | ''; text: string}>({
        type: '',
        text: '',
    });
    const [providerPage, setProviderPage] = useState(1);
    const [showInactive, setShowInactive] = useState(false);
    const [providerSort, setProviderSort] = useState<{key: ProviderSortKey | null; direction: 'asc' | 'desc'}>({
        key: null,
        direction: 'asc',
    });
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestSearchRef = useRef('');
    const logoModalFileRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
        orgModulesRef.current = orgModules;
    }, [orgModules]);
    useEffect(() => {
        providerModulesRef.current = providerModules;
    }, [providerModules]);
    useEffect(() => {
        patientOrgModulesRef.current = patientOrgModules;
    }, [patientOrgModules]);
    useEffect(() => {
        patientProviderModulesRef.current = patientProviderModules;
    }, [patientProviderModules]);
    useEffect(() => {
        const raw = (selectedOrg?.features as Record<string, unknown> | undefined)?.send_mld_patient_emails;
        setPatientEmailsToggle(typeof raw === 'boolean' ? raw : null);
        // `=== true`, as the API reads it: a non-boolean stored value keeps the block.
        setLoginViaMld((selectedOrg?.features as Record<string, unknown> | undefined)?.ohd_patients_login_via_mld === true);
    }, [selectedOrg]);

    const orgFeatures = selectedOrg?.features as PatientEmailFeatures | undefined;
    // Same resolver the API uses, so the pill can never claim emails are on
    // while the server suppresses them. Local state overrides the org's
    // stored value so the pill responds immediately to a click. The raw
    // features object is handed to the resolver untouched — it does its own
    // `=== true` comparisons, so a non-boolean stored value (e.g. a legacy
    // `"true"` string) resolves the same way here as it does on the API.
    const patientEmailsOn = !shouldSuppressPatientEmails(
        patientEmailsToggle === null ? orgFeatures : {...orgFeatures, send_mld_patient_emails: patientEmailsToggle}
    );
    const patientEmailsInherited =
        patientEmailsToggle === null && orgFeatures?.enable_employee_health_dashboard === true;

    const baseDomain = useMemo(() => {
        if (typeof window === 'undefined') return '';
        return getBaseDomain(window.location.hostname);
    }, []);

    const {
        switchToProvider: handleViewProviderConsole,
        switchingProviderId: providerSwitching,
        canSwitch: canViewProviderConsole,
        error: providerSwitchError,
    } = useProviderConsoleSwitch();

    const roleLabel = useCallback((roleId: number | null | undefined) => {
        if (roleId == null) return 'Unknown';
        if (isSuperAdministratorRole(roleId)) return 'Super Admin';
        if (isOrganizationRole(roleId)) return 'Org User';
        if (isProviderRole(roleId)) return 'Provider User';
        if (isPatientRole(roleId)) return 'Patient';
        return 'Unknown';
    }, []);

    const providerRoleDisplay = useCallback(
        (providerId: number, userId: number) => {
            const roles = providerRolesByProvider[providerId] ?? [];
            const assigned = providerUserRoleMap[userId] ?? [];
            const defaultRoleId = getDefaultProviderRoleId(roles);
            const roleId = assigned[0] ?? defaultRoleId ?? null;
            const label = roles.find((role) => role.id === roleId)?.name;
            return label ?? '—';
        },
        [providerRolesByProvider, providerUserRoleMap]
    );

    const providerUserGroups = useMemo(() => {
        const groups = new Map<number, {providerName: string; users: ProviderLoginUser[]}>();
        providers.filter((p) => p.is_active !== false).forEach((provider) => {
            groups.set(provider.id, {providerName: provider.name, users: []});
        });
        providerUsers.forEach((user) => {
            const key = user.provider_id;
            if (!groups.has(key)) {
                groups.set(key, {providerName: user.provider_name, users: []});
            }
            groups.get(key)!.users.push(user);
        });
        return Array.from(groups.entries())
            .map(([providerId, data]) => ({providerId, providerName: data.providerName, users: data.users}))
            .sort((a, b) => a.providerName.localeCompare(b.providerName));
    }, [providerUsers, providers]);

    const searchMutation = useMutation({
        mutationFn: (term: string) => AdminService.searchOrganizations(term),
        onSuccess: (data, variables) => {
            if (variables !== latestSearchRef.current) return;
            setResults(data ?? []);
            setStatusMessage({type: '', text: data?.length ? '' : 'No organizations found.'});
        },
        onError: () => {
            setStatusMessage({type: 'error', text: 'Failed to search organizations.'});
        },
    });

    const loadMutation = useMutation({
        mutationFn: (orgId: number) => AdminService.getOrganizationProviderConsole(orgId),
        onSuccess: (data: OrganizationProviderConsoleResponse) => {
            setSelectedOrg(data.organization);
            const mappedOrgModules = buildModuleMap(data.organization_modules, false);
            setOrgModules(mappedOrgModules);
            setPatientOrgModules(buildPatientOrgModuleMap(data.patient_organization_modules));
            setSubdomain(data.organization.subdomain ?? '');
            setSubdomainOriginal(data.organization.subdomain ?? '');
            setSubdomainMessage({type: '', text: ''});
            const nextLogo = data.organization_theme?.logo_url ?? '';
            setLogoUrl(nextLogo);
            setLogoUrlInput(nextLogo);
            setLogoMessage({type: '', text: ''});
            const nextCustomCss = data.organization_theme?.custom_css ?? '';
            const parsed = parseCustomCss(nextCustomCss);
            setCustomCssTokens(parsed.tokenValues);
            setCustomCssExtras(parsed.extras);
            setCustomCssOriginal(nextCustomCss);
            setCustomCssMessage({type: '', text: ''});

            setProviders(data.providers || []);
            setOrganizationUsers(data.organization_users || []);
            setProviderUsers(data.provider_users || []);
            const nextRolesByProvider: Record<number, ProviderRoleRow[]> = {};
            (data.provider_roles || []).forEach((role) => {
                if (!nextRolesByProvider[role.provider_id]) {
                    nextRolesByProvider[role.provider_id] = [];
                }
                nextRolesByProvider[role.provider_id].push(role);
            });
            Object.values(nextRolesByProvider).forEach((roles) => {
                roles.sort((a, b) => {
                    if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
                    return a.name.localeCompare(b.name);
                });
            });
            setProviderRolesByProvider(nextRolesByProvider);

            const nextUserRoleMap: Record<number, number[]> = {};
            (data.provider_user_roles || []).forEach((row) => {
                if (!nextUserRoleMap[row.user_id]) {
                    nextUserRoleMap[row.user_id] = [];
                }
                nextUserRoleMap[row.user_id].push(row.role_id);
            });
            Object.keys(nextUserRoleMap).forEach((userId) => {
                const unique = Array.from(new Set(nextUserRoleMap[Number(userId)]));
                nextUserRoleMap[Number(userId)] = unique;
            });
            setProviderUserRoleMap(nextUserRoleMap);
            const hasOrgUsers = (data.organization_users || []).length > 0;
            setOrgUserForm({
                first_name: '',
                last_name: '',
                email: hasOrgUsers ? '' : data.organization.support_email ?? '',
                phone: hasOrgUsers ? '' : formatPhoneFromRaw(data.organization.support_phone),
            });
            setOrgAddOpen(false);
            const providerUserCounts = (data.provider_users || []).reduce<Record<number, number>>((acc, user) => {
                acc[user.provider_id] = (acc[user.provider_id] ?? 0) + 1;
                return acc;
            }, {});
            const nextProviderForms: Record<number, UserFormState> = {};
            const nextProviderDrafts: Record<number, string> = {};
            const nextProviderRoleSelections: Record<number, number | null> = {};
            const nextProviderAddOpen: Record<number, boolean> = {};
            (data.providers || []).forEach((provider) => {
                const parsedName = parseContactName(provider.contact_name);
                const hasProviderUsers = (providerUserCounts[provider.id] ?? 0) > 0;
                nextProviderForms[provider.id] = {
                    first_name: hasProviderUsers ? '' : parsedName.first_name,
                    last_name: hasProviderUsers ? '' : parsedName.last_name,
                    email: hasProviderUsers ? '' : provider.contact_email ?? '',
                    phone: hasProviderUsers ? '' : formatPhoneFromRaw(provider.contact_phone),
                };
                nextProviderDrafts[provider.id] = provider.name ?? '';
                const defaultRoleId = getDefaultProviderRoleId(nextRolesByProvider[provider.id]);
                nextProviderRoleSelections[provider.id] = defaultRoleId ?? null;
                nextProviderAddOpen[provider.id] = false;
            });
            setProviderUserForms(nextProviderForms);
            setProviderNameDrafts(nextProviderDrafts);
            setProviderUserRoleSelections(nextProviderRoleSelections);
            setProviderAddOpen(nextProviderAddOpen);
            setOrgUserEdit(null);
            setProviderUserEdit(null);
            setOrgUserMessage({type: '', text: ''});
            setOrgUserEditMessage({type: '', text: ''});
            setProviderUserMessages({});
            setProviderUserEditMessage({type: '', text: ''});

            const nextProviderModules: Record<number, ModuleMap> = {};
            Object.entries(data.provider_modules || {}).forEach(([providerId, moduleRows]) => {
                nextProviderModules[Number(providerId)] = buildModuleMap(moduleRows, false);
            });

            (data.providers || []).forEach((provider) => {
                if (!nextProviderModules[provider.id]) {
                    nextProviderModules[provider.id] = buildModuleMap(undefined, false);
                }
            });

            setProviderModules(nextProviderModules);

            const nextPatientProviderModules: Record<number, PatientProviderModuleMap> = {};
            Object.entries(data.patient_provider_modules || {}).forEach(([providerId, moduleRows]) => {
                nextPatientProviderModules[Number(providerId)] = buildPatientProviderModuleMap(moduleRows);
            });
            (data.providers || []).forEach((provider) => {
                if (!nextPatientProviderModules[provider.id]) {
                    nextPatientProviderModules[provider.id] = {};
                }
            });
            setPatientProviderModules(nextPatientProviderModules);
            setStatusMessage({type: '', text: ''});
        },
        onError: () => {
            setStatusMessage({type: 'error', text: 'Failed to load provider console configuration.'});
        },
    });

    const logoUploadMutation = useMutation({
        mutationFn: (payload: {orgId: number; file: File}) => AdminService.uploadOrganizationLogo(payload.orgId, payload.file),
        onSuccess: (data) => {
            setLogoUrl(data.logo_url);
            setLogoUrlInput(data.logo_url);
            setLogoMessage({type: 'success', text: 'Logo uploaded successfully.'});
            if (logoModalPreview) URL.revokeObjectURL(logoModalPreview);
            setLogoModalFile(null);
            setLogoModalPreview(null);
            setLogoModalOpen(false);
        },
        onError: (error: unknown) => {
            // Surface the backend error verbatim (e.g. "Image storage is
            // temporarily unavailable…") instead of a generic toast.
            const beMessage =
                (error as {data?: {error?: string}})?.data?.error ||
                (error instanceof Error ? error.message : '');
            setLogoMessage({type: 'error', text: beMessage || 'Failed to upload logo.'});
        },
    });

    const logoUrlMutation = useMutation({
        mutationFn: (payload: {orgId: number; logoUrl: string}) =>
            AdminService.updateOrganizationLogoUrl(payload.orgId, payload.logoUrl),
        onSuccess: (data) => {
            setLogoUrl(data.logo_url);
            setLogoUrlInput(data.logo_url);
            setLogoMessage({type: 'success', text: 'Logo url saved.'});
            setLogoModalOpen(false);
        },
        onError: () => {
            setLogoMessage({type: 'error', text: 'Failed to update logo url.'});
        },
    });

    const logoDeleteMutation = useMutation({
        mutationFn: (orgId: number) => AdminService.deleteOrganizationLogo(orgId),
        onSuccess: () => {
            setLogoUrl('');
            setLogoUrlInput('');
            setLogoMessage({type: 'success', text: 'Logo removed.'});
        },
        onError: () => {
            setLogoMessage({type: 'error', text: 'Failed to delete logo.'});
        },
    });


    const saveMutation = useMutation({
        mutationFn: (payload: {orgId: number; body: OrganizationProviderConsoleUpdateRequest}) =>
            AdminService.updateOrganizationProviderConsole(payload.orgId, payload.body),
        onSuccess: () => {
            setStatusMessage({type: 'success', text: 'Provider console settings updated.'});
        },
        onError: () => {
            setStatusMessage({type: 'error', text: 'Failed to update provider console settings.'});
        },
    });

    const disableMutation = useMutation({
        mutationFn: (orgId: number) => AdminService.disableOrganizationProviderConsole(orgId),
        onSuccess: () => {
            const disabledModules = buildModuleMap(undefined, false);
            setOrgModules(disabledModules);

            const nextProviderModules: Record<number, ModuleMap> = {};
            providers.forEach((provider) => {
                nextProviderModules[provider.id] = buildModuleMap(undefined, false);
            });
            setProviderModules(nextProviderModules);
            setStatusMessage({type: 'success', text: 'All provider console modules disabled.'});
        },
        onError: () => {
            setStatusMessage({type: 'error', text: 'Failed to disable provider console modules.'});
        },
    });

    const subdomainMutation = useMutation({
        mutationFn: (payload: {orgId: number; subdomain: string}) =>
            AdminService.updateOrganizationSubdomain(payload.orgId, payload.subdomain),
        onSuccess: (data) => {
            setSubdomainOriginal(data.subdomain);
            setSubdomainMessage({type: 'success', text: 'Subdomain updated.'});
            setSelectedOrg((prev) => (prev ? {...prev, subdomain: data.subdomain} : prev));
        },
        onError: (error: unknown) => {
            setSubdomainMessage({type: 'error', text: getErrorMessage(error, 'Failed to update subdomain.')});
        },
    });

    const customCssMutation = useMutation({
        mutationFn: (payload: {orgId: number; customCss: string}) =>
            AdminService.updateOrganizationCustomCss(payload.orgId, payload.customCss),
        onSuccess: (data) => {
            const saved = data.custom_css ?? '';
            const parsed = parseCustomCss(saved);
            setCustomCssTokens(parsed.tokenValues);
            setCustomCssExtras(parsed.extras);
            setCustomCssOriginal(saved);
            setCustomCssMessage({
                type: 'success',
                text: saved ? 'Custom CSS saved.' : 'Custom CSS cleared.',
            });
        },
        onError: (error: unknown) => {
            setCustomCssMessage({type: 'error', text: getErrorMessage(error, 'Failed to save custom CSS.')});
        },
    });

    const createOrgUserMutation = useMutation({
        mutationFn: (payload: {orgId: number; body: UserFormState}) =>
            AdminService.createOrganizationUser(payload.orgId, payload.body),
        onSuccess: () => {
            setOrgUserMessage({type: 'success', text: 'Organization user created.'});
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setOrgUserMessage({type: 'error', text: getErrorMessage(error, 'Failed to create organization user.')});
        },
    });

    const createProviderUserMutation = useMutation({
        mutationFn: (payload: {orgId: number; providerId: number; body: ProviderUserPayload}) =>
            AdminService.createProviderUser(payload.orgId, payload.providerId, payload.body),
        onSuccess: (_data, variables) => {
            setProviderUserMessages((prev) => ({
                ...prev,
                [variables.providerId]: {type: 'success', text: 'Provider user created.'},
            }));
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown, variables) => {
            setProviderUserMessages((prev) => ({
                ...prev,
                [variables.providerId]: {type: 'error', text: getErrorMessage(error, 'Failed to create provider user.')},
            }));
        },
    });

    const updateOrgUserMutation = useMutation({
        mutationFn: (payload: {orgId: number; userId: number; body: UserFormState}) =>
            AdminService.updateOrganizationUser(payload.orgId, payload.userId, payload.body),
        onSuccess: () => {
            setOrgUserEditMessage({type: 'success', text: 'Organization user updated.'});
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setOrgUserEditMessage({type: 'error', text: getErrorMessage(error, 'Failed to update organization user.')});
        },
    });

    const deleteOrgUserMutation = useMutation({
        mutationFn: (payload: {orgId: number; userId: number}) =>
            AdminService.deleteOrganizationUser(payload.orgId, payload.userId),
        onSuccess: () => {
            setOrgUserEditMessage({type: 'success', text: 'Organization user deleted.'});
            setOrgUserEdit(null);
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setOrgUserEditMessage({type: 'error', text: getErrorMessage(error, 'Failed to delete organization user.')});
        },
    });

    const updateProviderUserMutation = useMutation({
        mutationFn: (payload: {orgId: number; providerId: number; userId: number; body: ProviderUserPayload}) =>
            AdminService.updateProviderUser(payload.orgId, payload.providerId, payload.userId, payload.body),
        onSuccess: () => {
            setProviderUserEditMessage({type: 'success', text: 'Provider user updated.'});
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setProviderUserEditMessage({type: 'error', text: getErrorMessage(error, 'Failed to update provider user.')});
        },
    });

    const deleteProviderUserMutation = useMutation({
        mutationFn: (payload: {orgId: number; providerId: number; userId: number}) =>
            AdminService.deleteProviderUser(payload.orgId, payload.providerId, payload.userId),
        onSuccess: (_data, variables) => {
            setProviderUserEditMessage({type: 'success', text: 'Provider user removed.'});
            if (providerUserEdit?.id === variables.userId) {
                setProviderUserEdit(null);
            }
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setProviderUserEditMessage({type: 'error', text: getErrorMessage(error, 'Failed to remove provider user.')});
        },
    });

    // Provider rename persists to ELIS before updating local provider records.
    const updateProviderNameMutation = useMutation({
        mutationFn: (payload: {orgId: number; providerId: number; name: string}) =>
            AdminService.updateProviderName(payload.orgId, payload.providerId, payload.name),
        onMutate: (payload) => {
            setProviderNameSaving(payload.providerId);
        },
        onSuccess: (_data, variables) => {
            setProviderNameEditing((prev) => ({...prev, [variables.providerId]: false}));
            setProviderNameDrafts((prev) => ({...prev, [variables.providerId]: variables.name}));
            setStatusMessage({type: 'success', text: 'Provider name updated.'});
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setStatusMessage({type: 'error', text: getErrorMessage(error, 'Failed to update provider name.')});
        },
        onSettled: () => {
            setProviderNameSaving(null);
        },
    });

    const addProviderMutation = useMutation({
        mutationFn: (payload: {orgId: number; name: string; npi?: string}) =>
            AdminService.addProvider(payload.orgId, payload.name, payload.npi),
        onSuccess: () => {
            setAddProviderName('');
            setAddProviderNpi('');
            setAddProviderOpen(false);
            setAddProviderMessage({type: 'success', text: 'Provider added.'});
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setAddProviderMessage({type: 'error', text: getErrorMessage(error, 'Failed to add provider.')});
        },
    });

    const deleteProviderMutation = useMutation({
        mutationFn: (payload: {orgId: number; providerId: number; targetProviderId: number}) =>
            AdminService.deleteProvider(payload.orgId, payload.providerId, payload.targetProviderId),
        onSuccess: (data) => {
            setDeleteProviderOpen(false);
            setDeleteProviderId(null);
            setDeleteProviderName('');
            setDeleteProviderTarget(null);
            const warning = data.warning ? ` Warning: ${data.warning}` : '';
            setDeleteProviderMessage({type: 'success', text: `Provider deleted and data reassigned.${warning}`});
            if (selectedOrg) {
                loadMutation.mutate(selectedOrg.id);
            }
        },
        onError: (error: unknown) => {
            setDeleteProviderMessage({type: 'error', text: getErrorMessage(error, 'Failed to delete provider.')});
        },
    });

    const isBusy = searchMutation.isPending || loadMutation.isPending || saveMutation.isPending || disableMutation.isPending;
    const logoBusy = logoUploadMutation.isPending || logoUrlMutation.isPending || logoDeleteMutation.isPending;
    const isSubdomainBusy = subdomainMutation.isPending;

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const debouncedSearch = useCallback(
        (term: string) => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }

            const trimmed = term.trim();
            if (!trimmed) {
                setResults([]);
                setStatusMessage({type: '', text: ''});
                return;
            }

            latestSearchRef.current = trimmed;
            searchTimeoutRef.current = setTimeout(() => {
                searchMutation.mutate(trimmed);
            }, 300);
        },
        [searchMutation]
    );

    const handleSearch = useCallback(
        (event: React.FormEvent) => {
            event.preventDefault();
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            const term = query.trim();
            if (!term) {
                setStatusMessage({type: 'error', text: 'Enter a name, subdomain, display name, or email to search.'});
                return;
            }
            setStatusMessage({type: '', text: ''});
            latestSearchRef.current = term;
            searchMutation.mutate(term);
        },
        [query, searchMutation]
    );

    const handleQueryChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const value = event.target.value;
            setQuery(value);
            debouncedSearch(value);
        },
        [debouncedSearch]
    );

    const handleSelectOrg = useCallback(
        (org: OrganizationSearchResult) => {
            const label = org.display_name?.trim() || org.name;
            setQuery(label);
            setResults([]);
            setSelectedOrg(org);
            setOrgModules(null);
            setProviders([]);
            setProviderModules({});
            setPatientOrgModules(null);
            setPatientProviderModules({});
            setProviderRolesByProvider({});
            setProviderUserRoleMap({});
            setOrganizationUsers([]);
            setProviderUsers([]);
            setSubdomain('');
            setSubdomainOriginal('');
            setProviderGroupsOpen({});
            setProviderNameEditing({});
            setProviderNameDrafts({});
            setStatusMessage({type: '', text: ''});
            setSubdomainMessage({type: '', text: ''});
            setOrgUserMessage({type: '', text: ''});
            setOrgUserEditMessage({type: '', text: ''});
            setProviderUserMessages({});
            setProviderUserEditMessage({type: '', text: ''});
            setOrgUserEdit(null);
            setProviderUserEdit(null);
            setOrgUserForm({
                first_name: '',
                last_name: '',
                email: org.support_email ?? '',
                phone: formatPhoneFromRaw(org.support_phone),
            });
            setOrgAddOpen(true);
            setProviderUserRoleSelections({});
            setProviderAddOpen({});
            loadMutation.mutate(org.id);
            onSelectOrg?.(org);
        },
        [loadMutation, onSelectOrg]
    );

    useEffect(() => {
        if (!selectedOrgOverride) return;
        if (selectedOrg?.id === selectedOrgOverride.id) {
            // Same org — sync mutable fields (e.g. name) without a full reload.
            if (selectedOrg.name !== selectedOrgOverride.name || selectedOrg.display_name !== selectedOrgOverride.display_name) {
                setSelectedOrg(selectedOrgOverride);
            }
            return;
        }
        handleSelectOrg(selectedOrgOverride);
    }, [handleSelectOrg, selectedOrg?.id, selectedOrg?.name, selectedOrg?.display_name, selectedOrgOverride]);

    const persistOrgModules = useCallback(
        (nextOrgModules: ModuleMap) => {
            if (!selectedOrg) return;
            const organization_modules = moduleMapToArray(nextOrgModules);
            const provider_modules: OrganizationProviderModulesInput[] = providers.map((provider) => {
                const moduleMap = providerModulesRef.current[provider.id] ?? buildModuleMap(undefined, false);
                return {provider_id: provider.id, modules: moduleMapToArray(moduleMap)};
            });
            saveMutation.mutate({
                orgId: selectedOrg.id,
                body: {organization_modules, provider_modules},
            });
        },
        [providers, saveMutation, selectedOrg]
    );

    const handleOrgModuleToggle = useCallback(
        (moduleKey: ProviderConsoleModuleKey, enabled: boolean) => {
            const current = orgModulesRef.current;
            if (!current) return;
            const nextOrgModules = {...current, [moduleKey]: enabled};
            setOrgModules(nextOrgModules);
            persistOrgModules(nextOrgModules);
        },
        [persistOrgModules]
    );

    const persistProviderModules = useCallback(
        (nextProviderModules: Record<number, ModuleMap>) => {
            if (!selectedOrg) return;
            const currentOrgModules = orgModulesRef.current;
            if (!currentOrgModules) return;
            const organization_modules = moduleMapToArray(currentOrgModules);
            const provider_modules: OrganizationProviderModulesInput[] = providers.map((provider) => {
                const moduleMap = nextProviderModules[provider.id] ?? buildModuleMap(undefined, false);
                return {provider_id: provider.id, modules: moduleMapToArray(moduleMap)};
            });
            saveMutation.mutate({
                orgId: selectedOrg.id,
                body: {organization_modules, provider_modules},
            });
        },
        [providers, saveMutation, selectedOrg]
    );

    const handleProviderModuleToggle = useCallback(
        (providerId: number, moduleKey: ProviderConsoleModuleKey, enabled: boolean) => {
            if (!selectedOrg) return;
            const currentOrgModules = orgModulesRef.current;
            if (!currentOrgModules) return;
            const current = providerModulesRef.current;
            const nextProviderModules = {
                ...current,
                [providerId]: {
                    ...(current[providerId] ?? buildModuleMap(undefined, false)),
                    [moduleKey]: enabled,
                },
            };
            setProviderModules(nextProviderModules);
            persistProviderModules(nextProviderModules);
        },
        [persistProviderModules, selectedOrg]
    );

    /**
     * Patient-dashboard saves send ONLY the patient_* arrays. Omitting the
     * provider-console arrays is load-bearing: their presence is what makes
     * the API touch console entitlements. Org level goes out complete (all
     * nine modules) so the API can clamp/materialize against it; provider
     * level goes out sparsely (explicit rows only) so untouched cells never
     * materialize provider_modules rows.
     */
    const persistPatientModules = useCallback(
        (nextOrgModules: PatientModuleMap, nextProviderModules: Record<number, PatientProviderModuleMap>) => {
            if (!selectedOrg) return;
            const patient_organization_modules = patientOrgModuleMapToArray(nextOrgModules);
            const patient_provider_modules: OrganizationProviderModulesInput[] = providers
                .map((provider) => ({
                    provider_id: provider.id,
                    modules: patientProviderModuleMapToArray(nextProviderModules[provider.id] ?? {}),
                }))
                .filter((entry) => entry.modules.length > 0);
            saveMutation.mutate({
                orgId: selectedOrg.id,
                body: {patient_organization_modules, patient_provider_modules},
            });
        },
        [providers, saveMutation, selectedOrg]
    );

    const handlePatientOrgModuleToggle = useCallback(
        (moduleKey: PatientDashboardModuleKey, enabled: boolean) => {
            const current = patientOrgModulesRef.current;
            if (!current) return;
            const nextOrgModules = {...current, [moduleKey]: enabled};
            setPatientOrgModules(nextOrgModules);
            persistPatientModules(nextOrgModules, patientProviderModulesRef.current);
        },
        [persistPatientModules]
    );

    const handlePatientProviderModuleToggle = useCallback(
        (providerId: number, moduleKey: PatientDashboardModuleKey, enabled: boolean) => {
            if (!selectedOrg) return;
            const currentOrgModules = patientOrgModulesRef.current;
            if (!currentOrgModules) return;
            const current = patientProviderModulesRef.current;
            const nextProviderModules = {
                ...current,
                [providerId]: {
                    ...(current[providerId] ?? {}),
                    [moduleKey]: enabled,
                },
            };
            setPatientProviderModules(nextProviderModules);
            persistPatientModules(currentOrgModules, nextProviderModules);
        },
        [persistPatientModules, selectedOrg]
    );

    const handlePatientEmailsToggle = useCallback(async () => {
        if (!selectedOrg) return;
        const next = !patientEmailsOn;
        const previous = patientEmailsToggle;
        setPatientEmailsToggle(next);
        try {
            const res = await AdminService.updateOrganizationFeatures(selectedOrg.id, {send_mld_patient_emails: next});
            // Adopt the server's authoritative merged features so a later
            // reload (which replaces `selectedOrg` wholesale) can't resurrect
            // the pre-write value and fight this optimistic state.
            setSelectedOrg((prev) => (prev ? {...prev, features: res.features} : prev));
            toast.success(next ? 'MLD patient emails enabled' : 'MLD patient emails turned off');
        } catch (err) {
            setPatientEmailsToggle(previous);
            toast.error(getErrorMessage(err, 'Failed to update the patient email setting'));
        }
    }, [selectedOrg, patientEmailsOn, patientEmailsToggle]);

    const handleLoginViaMldToggle = useCallback(async () => {
        if (!selectedOrg) return;
        const next = !loginViaMld;
        setLoginViaMld(next);
        try {
            const res = await AdminService.updateOrganizationFeatures(selectedOrg.id, {ohd_patients_login_via_mld: next});
            setSelectedOrg((prev) => (prev ? {...prev, features: res.features} : prev));
            toast.success(
                next
                    ? 'Login via MLD on — OHD patients can now get MLD login emails'
                    : 'Login via MLD off — OHD patients sign in through OHD'
            );
        } catch (err) {
            setLoginViaMld(!next);
            toast.error(getErrorMessage(err, 'Failed to update the Login via MLD setting'));
        }
    }, [selectedOrg, loginViaMld]);

    const handleDisableAll = useCallback(() => {
        if (!selectedOrg) return;
        disableMutation.mutate(selectedOrg.id);
    }, [disableMutation, selectedOrg]);

    const handleDisableProviderModules = useCallback(() => {
        if (!selectedOrg || !orgModulesRef.current) return;
        const nextProviderModules: Record<number, ModuleMap> = {};
        providers.forEach((provider) => {
            nextProviderModules[provider.id] = buildModuleMap(undefined, false);
        });
        setProviderModules(nextProviderModules);
        persistProviderModules(nextProviderModules);
    }, [persistProviderModules, providers, selectedOrg]);

    const handleSubdomainChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value.toLowerCase();
        setSubdomain(value);
        setSubdomainMessage({type: '', text: ''});
    }, []);

    const handleSubdomainSave = useCallback(() => {
        if (!selectedOrg) return;
        const trimmed = subdomain.trim();
        if (!trimmed) {
            setSubdomainMessage({type: 'error', text: 'Subdomain is required.'});
            return;
        }
        if (!SUBDOMAIN_PATTERN.test(trimmed)) {
            setSubdomainMessage({
                type: 'error',
                text: 'Subdomain must be 1-63 chars, lowercase letters, numbers, or hyphens.',
            });
            return;
        }
        subdomainMutation.mutate({orgId: selectedOrg.id, subdomain: trimmed});
    }, [selectedOrg, subdomain, subdomainMutation]);

    const handleCustomCssTokenChange = useCallback(
        (name: string, hex: string) => {
            setCustomCssTokens((prev) => ({...prev, [name]: hex}));
            if (customCssMessage.text) setCustomCssMessage({type: '', text: ''});
        },
        [customCssMessage.text]
    );

    const handleCustomCssTokenReset = useCallback(
        (name: string) => {
            setCustomCssTokens((prev) => {
                const next = {...prev};
                delete next[name];
                return next;
            });
            if (customCssMessage.text) setCustomCssMessage({type: '', text: ''});
        },
        [customCssMessage.text]
    );

    const handleCustomCssExtrasChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            setCustomCssExtras(event.target.value);
            if (customCssMessage.text) setCustomCssMessage({type: '', text: ''});
        },
        [customCssMessage.text]
    );

    const customCssBuilt = useMemo(
        () => serializeCustomCss(customCssTokens, customCssExtras),
        [customCssTokens, customCssExtras]
    );

    const isCustomCssDirty = customCssBuilt.trim() !== customCssOriginal.trim();

    const handleCustomCssSave = useCallback(() => {
        if (!selectedOrg) return;
        customCssMutation.mutate({orgId: selectedOrg.id, customCss: customCssBuilt});
    }, [selectedOrg, customCssBuilt, customCssMutation]);

    const handleCustomCssClear = useCallback(() => {
        if (!selectedOrg) return;
        customCssMutation.mutate({orgId: selectedOrg.id, customCss: ''});
    }, [selectedOrg, customCssMutation]);

    const handleCopyUrl = useCallback(async () => {
        if (!subdomain || !baseDomain) return;
        const url = `https://${subdomain}.${baseDomain}`;

        const tryClipboardApi = async () => {
            if (typeof navigator === 'undefined' || !navigator.clipboard) return false;
            if (typeof window !== 'undefined' && window.isSecureContext === false) return false;
            await navigator.clipboard.writeText(url);
            return true;
        };

        const tryLegacyCopy = () => {
            if (typeof document === 'undefined') return false;
            const textarea = document.createElement('textarea');
            textarea.value = url;
            textarea.setAttribute('readonly', 'true');
            textarea.style.position = 'absolute';
            textarea.style.left = '-9999px';
            document.body.appendChild(textarea);
            textarea.select();
            const success = document.execCommand('copy');
            document.body.removeChild(textarea);
            return success;
        };

        try {
            const copied = (await tryClipboardApi()) || tryLegacyCopy();
            if (copied) {
                setSubdomainMessage({type: 'success', text: 'Copied URL to clipboard.'});
            } else {
                setSubdomainMessage({type: 'error', text: 'Copy is not supported in this browser context.'});
            }
        } catch {
            setSubdomainMessage({type: 'error', text: 'Failed to copy URL.'});
        }
    }, [baseDomain, subdomain]);

    const handleLogoUrlInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setLogoUrlInput(event.target.value);
        setLogoMessage({type: '', text: ''});
    }, []);

    const handleLogoUrlSave = useCallback(() => {
        if (!selectedOrg) return;
        const trimmed = logoUrlInput.trim();
        if (!trimmed) {
            setLogoMessage({type: 'error', text: 'Logo url is required.'});
            return;
        }
        logoUrlMutation.mutate({orgId: selectedOrg.id, logoUrl: trimmed});
    }, [logoUrlInput, logoUrlMutation, selectedOrg]);

    const handleLogoDelete = useCallback(() => {
        if (!selectedOrg) return;
        logoDeleteMutation.mutate(selectedOrg.id);
    }, [logoDeleteMutation, selectedOrg]);

    const handleLogoModalClose = useCallback(() => {
        if (logoModalPreview) {
            URL.revokeObjectURL(logoModalPreview);
        }
        setLogoModalFile(null);
        setLogoModalPreview(null);
        setLogoModalDragOver(false);
        setLogoModalOpen(false);
    }, [logoModalPreview]);

    const handleLogoModalDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setLogoModalDragOver(false);
        const file = e.dataTransfer.files[0];
        if (!file) return;
        if (logoModalPreview) URL.revokeObjectURL(logoModalPreview);
        setLogoModalFile(file);
        setLogoModalPreview(URL.createObjectURL(file));
    }, [logoModalPreview]);

    const handleLogoModalFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (!file) return;
            if (logoModalPreview) URL.revokeObjectURL(logoModalPreview);
            setLogoModalFile(file);
            setLogoModalPreview(URL.createObjectURL(file));
        },
        [logoModalPreview]
    );

    const handleLogoModalUpload = useCallback(() => {
        if (!selectedOrg || !logoModalFile) return;
        setLogoMessage({type: '', text: ''});
        logoUploadMutation.mutate({orgId: selectedOrg.id, file: logoModalFile});
    }, [logoModalFile, logoUploadMutation, selectedOrg]);

    const handleToggleProviderGroup = useCallback((providerId: number) => {
        setProviderGroupsOpen((prev) => ({...prev, [providerId]: !prev[providerId]}));
    }, []);

    const handleToggleOrgAdd = useCallback(() => {
        setOrgAddOpen((prev) => !prev);
    }, []);

    const handleToggleProviderAdd = useCallback((providerId: number) => {
        setProviderAddOpen((prev) => ({...prev, [providerId]: !prev[providerId]}));
    }, []);

    const handleEditProviderName = useCallback((providerId: number, name: string) => {
        setProviderNameEditing((prev) => ({...prev, [providerId]: true}));
        setProviderNameDrafts((prev) => ({...prev, [providerId]: name}));
    }, []);

    const handleProviderNameChange = useCallback((providerId: number, value: string) => {
        setProviderNameDrafts((prev) => ({...prev, [providerId]: value}));
    }, []);

    const handleCancelProviderName = useCallback((providerId: number, name: string) => {
        setProviderNameEditing((prev) => ({...prev, [providerId]: false}));
        setProviderNameDrafts((prev) => ({...prev, [providerId]: name}));
    }, []);

    const handleSaveProviderName = useCallback(
        (providerId: number) => {
            if (!selectedOrg) return;
            const draft = providerNameDrafts[providerId] ?? '';
            const trimmed = draft.trim();
            if (!trimmed) {
                setStatusMessage({type: 'error', text: 'Provider name is required.'});
                return;
            }
            updateProviderNameMutation.mutate({orgId: selectedOrg.id, providerId, name: trimmed});
        },
        [providerNameDrafts, selectedOrg, updateProviderNameMutation]
    );

    const handleAddProvider = useCallback(() => {
        if (!selectedOrg) return;
        const trimmed = addProviderName.trim();
        if (!trimmed) {
            setAddProviderMessage({type: 'error', text: 'Provider name is required.'});
            return;
        }
        const npiTrimmed = addProviderNpi.trim() || undefined;
        addProviderMutation.mutate({orgId: selectedOrg.id, name: trimmed, npi: npiTrimmed});
    }, [addProviderName, addProviderNpi, selectedOrg, addProviderMutation]);

    const handleOpenDeleteProvider = useCallback((providerId: number, providerName: string) => {
        setDeleteProviderId(providerId);
        setDeleteProviderName(providerName);
        setDeleteProviderTarget(null);
        setDeleteProviderMessage({type: '', text: ''});
        setDeleteProviderOpen(true);
    }, []);

    const handleConfirmDeleteProvider = useCallback(() => {
        if (!selectedOrg || !deleteProviderId || !deleteProviderTarget) return;
        deleteProviderMutation.mutate({
            orgId: selectedOrg.id,
            providerId: deleteProviderId,
            targetProviderId: deleteProviderTarget,
        });
    }, [selectedOrg, deleteProviderId, deleteProviderTarget, deleteProviderMutation]);

    const handleCancelDeleteProvider = useCallback(() => {
        setDeleteProviderOpen(false);
        setDeleteProviderId(null);
        setDeleteProviderName('');
        setDeleteProviderTarget(null);
        setDeleteProviderMessage({type: '', text: ''});
    }, []);

    const handleOrgUserChange = useCallback((field: keyof UserFormState, value: string) => {
        const nextValue = field === 'phone' ? formatPhoneInput(value) : value;
        setOrgUserForm((prev) => ({...prev, [field]: nextValue}));
        setOrgUserMessage({type: '', text: ''});
        setOrgUserFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = {...prev};
            delete next[field];
            return next;
        });
    }, []);

    const handleOrgUserEditChange = useCallback((field: keyof UserFormState, value: string) => {
        const nextValue = field === 'phone' ? formatPhoneInput(value) : value;
        setOrgUserEdit((prev) => (prev ? {...prev, form: {...prev.form, [field]: nextValue}} : prev));
        setOrgUserEditMessage({type: '', text: ''});
        setOrgUserEditFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = {...prev};
            delete next[field];
            return next;
        });
    }, []);

    const handleProviderUserChange = useCallback((providerId: number, field: keyof UserFormState, value: string) => {
        const nextValue = field === 'phone' ? formatPhoneInput(value) : value;
        setProviderUserForms((prev) => ({
            ...prev,
            [providerId]: {
                ...(prev[providerId] ?? {first_name: '', last_name: '', email: '', phone: ''}),
                [field]: nextValue,
            },
        }));
        setProviderUserMessages((prev) => ({...prev, [providerId]: {type: '', text: ''}}));
        setProviderUserFieldErrors((prev) => {
            const current = prev[providerId];
            if (!current?.[field]) return prev;
            const next = {...current};
            delete next[field];
            if (Object.keys(next).length === 0) {
                const rest = {...prev};
                delete rest[providerId];
                return rest;
            }
            return {...prev, [providerId]: next};
        });
    }, []);

    const handleProviderUserEditChange = useCallback((field: keyof UserFormState, value: string) => {
        const nextValue = field === 'phone' ? formatPhoneInput(value) : value;
        setProviderUserEdit((prev) => (prev ? {...prev, form: {...prev.form, [field]: nextValue}} : prev));
        setProviderUserEditMessage({type: '', text: ''});
    }, []);

    const handleProviderRoleSelect = useCallback((providerId: number, roleId: number) => {
        setProviderUserRoleSelections((prev) => ({...prev, [providerId]: roleId}));
    }, []);

    const handleProviderEditRoleSelect = useCallback((roleId: number) => {
        setProviderUserEdit((prev) => (prev ? {...prev, role_id: roleId} : prev));
        setProviderUserEditMessage({type: '', text: ''});
    }, []);

    const handleCreateOrgUser = useCallback(() => {
        if (!selectedOrg) return;
        const missingFields: FormFieldErrors = {};
        if (!orgUserForm.first_name.trim()) missingFields.first_name = true;
        if (!orgUserForm.last_name.trim()) missingFields.last_name = true;
        if (!orgUserForm.email.trim()) missingFields.email = true;
        if (!orgUserForm.phone.trim()) missingFields.phone = true;
        if (Object.keys(missingFields).length > 0) {
            setOrgUserFieldErrors(missingFields);
            setOrgUserMessage({type: 'error', text: 'First name, last name, email, and phone are required.'});
            return;
        }
        setOrgUserFieldErrors({});
        const payload = {
            ...orgUserForm,
            phone: normalizePhoneForSave(orgUserForm.phone),
        };
        createOrgUserMutation.mutate({orgId: selectedOrg.id, body: payload});
    }, [createOrgUserMutation, orgUserForm, selectedOrg]);

    const handleCreateProviderUser = useCallback(
        (providerId: number) => {
            if (!selectedOrg) return;
            const form = providerUserForms[providerId] ?? {first_name: '', last_name: '', email: '', phone: ''};
            const missingFields: FormFieldErrors = {};
            if (!form.first_name.trim()) missingFields.first_name = true;
            if (!form.last_name.trim()) missingFields.last_name = true;
            if (!form.email.trim()) missingFields.email = true;
            if (!form.phone.trim()) missingFields.phone = true;
            if (Object.keys(missingFields).length > 0) {
                setProviderUserFieldErrors((prev) => ({...prev, [providerId]: missingFields}));
                setProviderUserMessages((prev) => ({
                    ...prev,
                    [providerId]: {
                        type: 'error',
                        text: 'First name, last name, email, and phone are required.',
                    },
                }));
                return;
            }
            setProviderUserFieldErrors((prev) => {
                if (!prev[providerId]) return prev;
                const rest = {...prev};
                delete rest[providerId];
                return rest;
            });
            const payload = {
                ...form,
                phone: normalizePhoneForSave(form.phone),
                role_ids: providerUserRoleSelections[providerId] ? [providerUserRoleSelections[providerId] as number] : [],
            };
            createProviderUserMutation.mutate({orgId: selectedOrg.id, providerId, body: payload});
        },
        [createProviderUserMutation, providerUserForms, providerUserRoleSelections, selectedOrg]
    );

    const handleEditOrgUser = useCallback((user: OrganizationLoginUser) => {
        setOrgUserEdit({
            id: user.id,
            form: {
                first_name: user.first_name ?? '',
                last_name: user.last_name ?? '',
                email: user.email ?? '',
                phone: formatPhoneFromRaw(user.phone),
            },
        });
        setOrgUserEditMessage({type: '', text: ''});
        setOrgUserEditFieldErrors({});
    }, []);

    const handleCancelOrgUserEdit = useCallback(() => {
        setOrgUserEdit(null);
        setOrgUserEditMessage({type: '', text: ''});
    }, []);

    const handleSaveOrgUserEdit = useCallback(() => {
        if (!selectedOrg || !orgUserEdit) return;
        const missingFields: FormFieldErrors = {};
        if (!orgUserEdit.form.first_name.trim()) missingFields.first_name = true;
        if (!orgUserEdit.form.last_name.trim()) missingFields.last_name = true;
        if (!orgUserEdit.form.email.trim()) missingFields.email = true;
        if (!orgUserEdit.form.phone.trim()) missingFields.phone = true;
        if (Object.keys(missingFields).length > 0) {
            setOrgUserEditFieldErrors(missingFields);
            setOrgUserEditMessage({type: 'error', text: 'First name, last name, email, and phone are required.'});
            return;
        }
        setOrgUserEditFieldErrors({});
        const payload = {...orgUserEdit.form, phone: normalizePhoneForSave(orgUserEdit.form.phone)};
        updateOrgUserMutation.mutate({orgId: selectedOrg.id, userId: orgUserEdit.id, body: payload});
    }, [orgUserEdit, selectedOrg, updateOrgUserMutation]);

    const handleEditProviderUser = useCallback((user: ProviderLoginUser) => {
        const assignedRoles = providerUserRoleMap[user.id] ?? [];
        const fallbackRoleId = getDefaultProviderRoleId(providerRolesByProvider[user.provider_id]);
        const nextRoleId = assignedRoles[0] ?? fallbackRoleId ?? null;
        setProviderUserEdit({
            id: user.id,
            providerId: user.provider_id,
            form: {
                first_name: user.first_name ?? '',
                last_name: user.last_name ?? '',
                email: user.email ?? '',
                phone: formatPhoneFromRaw(user.phone),
            },
            role_id: nextRoleId,
        });
        setProviderUserEditMessage({type: '', text: ''});
    }, [providerRolesByProvider, providerUserRoleMap]);

    const handleCancelProviderUserEdit = useCallback(() => {
        setProviderUserEdit(null);
        setProviderUserEditMessage({type: '', text: ''});
    }, []);

    const handleSaveProviderUserEdit = useCallback(() => {
        if (!selectedOrg || !providerUserEdit) return;
        const payload = {
            ...providerUserEdit.form,
            phone: normalizePhoneForSave(providerUserEdit.form.phone),
            role_ids: providerUserEdit.role_id ? [providerUserEdit.role_id] : [],
        };
        updateProviderUserMutation.mutate({
            orgId: selectedOrg.id,
            providerId: providerUserEdit.providerId,
            userId: providerUserEdit.id,
            body: payload,
        });
    }, [providerUserEdit, selectedOrg, updateProviderUserMutation]);

    const handleDeleteProviderUser = useCallback(
        (user: ProviderLoginUser) => {
            if (!selectedOrg) return;
            if (!window.confirm(`Remove ${user.email || 'this provider user'}?`)) return;
            deleteProviderUserMutation.mutate({
                orgId: selectedOrg.id,
                providerId: user.provider_id,
                userId: user.id,
            });
        },
        [deleteProviderUserMutation, selectedOrg]
    );

    const isSubdomainDirty = useMemo(() => {
        return subdomain.trim() !== subdomainOriginal.trim();
    }, [subdomain, subdomainOriginal]);

    const handleApplyOrgModules = useCallback(() => {
        if (!orgModules) return;
        const nextProviderModules: Record<number, ModuleMap> = {};
        providers.forEach((provider) => {
            nextProviderModules[provider.id] = {...orgModules};
        });
        setProviderModules(nextProviderModules);
        setStatusMessage({type: 'success', text: 'Applied organization modules to all providers.'});
    }, [orgModules, providers]);

    useEffect(() => {
        if (providerSwitchError) {
            setStatusMessage({type: 'error', text: providerSwitchError});
        }
    }, [providerSwitchError]);

    const handleProviderSort = useCallback((key: ProviderSortKey) => {
        setProviderSort((prev) => {
            if (prev.key === key) {
                return {key, direction: prev.direction === 'asc' ? 'desc' : 'asc'};
            }
            return {key, direction: 'asc'};
        });
    }, []);

    const statusBadge = useMemo(() => {
        if (!statusMessage.text) return null;
        const baseClass = 'rounded-lg px-4 py-3 text-sm';
        if (statusMessage.type === 'success') {
            return <div className={`${baseClass} bg-mld-primary/10 text-mld-primary`}>{statusMessage.text}</div>;
        }
        if (statusMessage.type === 'error') {
            return <div className={`${baseClass} bg-mld-danger/5 text-mld-danger`}>{statusMessage.text}</div>;
        }
        return <div className={`${baseClass} bg-mld-menu-background text-mld-text-secondary`}>{statusMessage.text}</div>;
    }, [statusMessage]);

    const showModulesSection = activeTab === 'all' || activeTab === 'modules';
    const showProvidersSection = activeTab === 'all' || activeTab === 'providers';
    const showSubdomainSection = activeTab === 'all' || activeTab === 'subdomain';
    const showLoginsSection = activeTab === 'all' || activeTab === 'logins';
    const rootClassName = showActionBar ? 'space-y-6 pb-24' : 'space-y-6';

    useEffect(() => {
        setProviderPage(1);
    }, [providerSort.direction, providerSort.key]);

    const sortedProviders = useMemo(() => {
        const sorted = [...providers];
        if (!providerSort.key || sorted.length <= 1) return sorted;
        const getSortValue = (provider: OrganizationProvider): string | number | boolean | null => {
            switch (providerSort.key) {
                case 'name':
                    return provider.name ?? '';
                case 'is_active':
                    return provider.is_active ?? false;
                default: {
                    const sortKey = providerSort.key as ProviderConsoleModuleKey | PatientDashboardModuleKey;
                    if (sortKey && sortKey.startsWith('patient.')) {
                        const patientKey = sortKey as PatientDashboardModuleKey;
                        const patientMap = patientProviderModules[provider.id] ?? {};
                        const orgEnabled = patientOrgModules ? Boolean(patientOrgModules[patientKey]) : true;
                        return orgEnabled && (patientMap[patientKey] ?? true);
                    }
                    const moduleMap = providerModules[provider.id] ?? buildModuleMap(undefined, false);
                    return Boolean(moduleMap[sortKey as ProviderConsoleModuleKey]);
                }
            }
        };

        const compareValues = (
            aValue: string | number | boolean | null,
            bValue: string | number | boolean | null
        ): number => {
            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return 1;
            if (bValue == null) return -1;
            if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
                return Number(aValue) - Number(bValue);
            }
            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return aValue - bValue;
            }
            return String(aValue).localeCompare(String(bValue), undefined, {numeric: true, sensitivity: 'base'});
        };

        sorted.sort((a, b) => {
            const primary = compareValues(getSortValue(a), getSortValue(b));
            if (primary !== 0) {
                return providerSort.direction === 'asc' ? primary : -primary;
            }
            return a.name.localeCompare(b.name);
        });

        return sorted;
    }, [patientOrgModules, patientProviderModules, providerModules, providerSort.direction, providerSort.key, providers]);

    const inactiveCount = sortedProviders.filter((p) => p.is_active === false).length;
    const visibleProviders = showInactive ? sortedProviders : sortedProviders.filter((p) => p.is_active !== false);

    const totalProviders = visibleProviders.length;
    const totalPages = Math.max(1, Math.ceil(totalProviders / PROVIDER_PAGE_SIZE));
    const currentPage = Math.min(providerPage, totalPages);
    const pageStart = totalProviders === 0 ? 0 : (currentPage - 1) * PROVIDER_PAGE_SIZE + 1;
    const pageEnd = Math.min(currentPage * PROVIDER_PAGE_SIZE, totalProviders);
    const paginatedProviders = visibleProviders.slice(pageStart === 0 ? 0 : pageStart - 1, pageEnd);

    useEffect(() => {
        setProviderPage((prev) => Math.min(prev, totalPages));
    }, [totalPages]);

    const providerSortIndicator = useCallback(
        (key: ProviderSortKey) => {
            const isActive = providerSort.key === key;
            const baseClass = 'h-3 w-3 transition-colors';
            return (
                <span className="flex flex-col items-center -space-y-1">
                    <ChevronUp
                        className={`${baseClass} ${
                            isActive && providerSort.direction === 'asc' ? 'text-mld-text-secondary' : 'text-mld-muted-text group-hover:text-mld-muted-text'
                        }`}
                    />
                    <ChevronDown
                        className={`${baseClass} ${
                            isActive && providerSort.direction === 'desc'
                                ? 'text-mld-text-secondary'
                                : 'text-mld-muted-text group-hover:text-mld-muted-text'
                        }`}
                    />
                </span>
            );
        },
        [providerSort.direction, providerSort.key]
    );

    return (
        <div className={rootClassName}>
            {showSearch ? (
                <Card>
                    <Card.Header>
                        <Card.Title className="flex items-center gap-2">
                            <Search className="h-5 w-5" />
                            <span>Organization Search</span>
                        </Card.Title>
                        <p className="text-sm text-mld-muted-text">
                            Search across all organizations to configure provider console modules and access.
                        </p>
                    </Card.Header>
                    <Card.Content>
                        <form onSubmit={handleSearch} className="space-y-4">
                            <div className="relative flex flex-col gap-3 md:flex-row">
                                <div className="relative flex-1">
                                    <input
                                        type="text"
                                        value={query}
                                        onChange={handleQueryChange}
                                        placeholder="Search by name, display name, subdomain, or org email"
                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                    />
                                    {(searchMutation.isPending || results.length > 0) && query.trim().length > 0 && (
                                        <div className="absolute z-10 mt-2 max-h-64 w-full overflow-auto rounded-md border border-mld-panel-border bg-white shadow-lg">
                                            {searchMutation.isPending && results.length === 0 ? (
                                                <div className="flex items-center gap-2 px-4 py-3 text-sm text-mld-text-secondary">
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                    Searching...
                                                </div>
                                            ) : null}
                                            {results.map((org) => (
                                                <button
                                                    type="button"
                                                    key={org.id}
                                                    className="flex w-full flex-col items-start gap-1 px-4 py-3 text-left text-sm text-mld-text-secondary hover:bg-mld-menu-background"
                                                    onClick={() => handleSelectOrg(org)}>
                                                    <span className="font-medium text-mld-text-primary">{org.display_name || org.name}</span>
                                                    <span className="text-xs text-mld-muted-text">
                                                        {org.subdomain} · {org.support_email ?? 'No support email'}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-white hover:bg-mld-primary-700"
                                    disabled={searchMutation.isPending}>
                                    {searchMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    Search
                                </button>
                            </div>
                        </form>
                        {statusBadge}
                    </Card.Content>
                </Card>
            ) : null}

            {showModulesSection && selectedOrg && orgModules && (
                <Card>
                    <Card.Header>
                        <Card.Title>Organization Modules (Purchased)</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <div className="flex flex-wrap gap-2">
                            {MODULE_KEYS.map((moduleKey) => {
                                const enabled = !!orgModules[moduleKey];
                                return (
                                    <button
                                        key={moduleKey}
                                        type="button"
                                        onClick={() => handleOrgModuleToggle(moduleKey, !enabled)}
                                        aria-pressed={enabled}
                                        className={clsx(
                                            'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition',
                                            enabled
                                                ? 'bg-mld-primary/10 text-mld-primary hover:bg-mld-primary/20'
                                                : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border'
                                        )}>
                                        {MODULE_LABELS[moduleKey]}
                                    </button>
                                );
                            })}
                        </div>
                    </Card.Content>
                </Card>
            )}

            {showModulesSection && selectedOrg && patientOrgModules && (
                <Card>
                    <Card.Header>
                        <Card.Title>Patient Dashboard Modules</Card.Title>
                        <p className="text-sm text-mld-muted-text">
                            Org-wide switches for this organization&apos;s patients. A dashboard module turned off here
                            is hidden for every patient, regardless of provider settings.
                        </p>
                    </Card.Header>
                    <Card.Content>
                        <div className="flex flex-wrap gap-2">
                            {PATIENT_MODULE_KEYS.map((moduleKey) => {
                                const enabled = !!patientOrgModules[moduleKey];
                                return (
                                    <button
                                        key={moduleKey}
                                        type="button"
                                        onClick={() => handlePatientOrgModuleToggle(moduleKey, !enabled)}
                                        aria-pressed={enabled}
                                        className={clsx(
                                            'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition',
                                            enabled
                                                ? 'bg-mld-primary/10 text-mld-primary hover:bg-mld-primary/20'
                                                : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border'
                                        )}>
                                        {PATIENT_MODULE_LABELS[moduleKey]}
                                    </button>
                                );
                            })}
                            <button
                                type="button"
                                onClick={handlePatientEmailsToggle}
                                aria-pressed={patientEmailsOn}
                                title={
                                    patientEmailsInherited
                                        ? 'Off because this org runs in OHD mode. Click to send MLD patient emails anyway.'
                                        : 'Controls MLD result notification emails for this org. Nothing else is affected yet — other transactional emails and the result SMS still send.'
                                }
                                className={clsx(
                                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition',
                                    patientEmailsOn
                                        ? 'bg-mld-primary/10 text-mld-primary hover:bg-mld-primary/20'
                                        : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border'
                                )}>
                                {patientEmailsInherited ? 'Patient Emails (OHD)' : 'Patient Emails'}
                            </button>
                            <button
                                type="button"
                                onClick={handleLoginViaMldToggle}
                                aria-pressed={loginViaMld}
                                title={
                                    loginViaMld
                                        ? "This org's OHD-imported patients sign in at MLD, so they get MLD's verification, password reset and 2FA emails."
                                        : 'OHD-imported patients sign in through OHD, so MLD sends them no verification, password reset or 2FA emails. Turn on if they sign in at MLD instead.'
                                }
                                className={clsx(
                                    'inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold transition',
                                    loginViaMld
                                        ? 'bg-mld-primary/10 text-mld-primary hover:bg-mld-primary/20'
                                        : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border'
                                )}>
                                Login via MLD
                            </button>
                        </div>
                    </Card.Content>
                </Card>
            )}

            {showProvidersSection && selectedOrg && providers.length > 0 && orgModules && (
                <Card>
                    <Card.Header className="flex items-center justify-between">
                        <Card.Title>Provider Module Access</Card.Title>
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={handleApplyOrgModules}
                            disabled={isBusy}>
                            Apply Org Modules to All Providers
                        </Button>
                    </Card.Header>
                    <Card.Content>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-mld-panel-border">
                                <thead className="bg-mld-menu-background">
                                    <tr>
                                        <th
                                            aria-sort={
                                                providerSort.key === 'name'
                                                    ? providerSort.direction === 'asc'
                                                        ? 'ascending'
                                                        : 'descending'
                                                    : 'none'
                                            }
                                            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                            <button
                                                type="button"
                                                onClick={() => handleProviderSort('name')}
                                                className="group inline-flex w-full items-center gap-2">
                                                Provider
                                                {providerSortIndicator('name')}
                                            </button>
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                            Console
                                        </th>
                                        <th
                                            aria-sort={
                                                providerSort.key === 'is_active'
                                                    ? providerSort.direction === 'asc'
                                                        ? 'ascending'
                                                        : 'descending'
                                                    : 'none'
                                            }
                                            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                            <button
                                                type="button"
                                                onClick={() => handleProviderSort('is_active')}
                                                className="group inline-flex w-full items-center gap-2">
                                                ELIS Active
                                                {providerSortIndicator('is_active')}
                                            </button>
                                        </th>
                                        {MODULE_KEYS.map((moduleKey) => (
                                            <th
                                                key={moduleKey}
                                                className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                <button
                                                    type="button"
                                                    onClick={() => handleProviderSort(moduleKey)}
                                                    className="group inline-flex w-full items-center justify-center gap-2">
                                                    {MODULE_LABELS[moduleKey]}
                                                    {providerSortIndicator(moduleKey)}
                                                </button>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-mld-panel-border bg-white">
                                    {paginatedProviders.map((provider) => {
                                        const moduleMap = providerModules[provider.id] ?? buildModuleMap(undefined, false);
                                        return (
                                            <tr key={provider.id} className={`hover:bg-mld-menu-background${provider.is_active === false ? ' opacity-50' : ''}`}>
                                                <td className="px-4 py-3 text-sm font-medium text-mld-text-primary">{provider.name}</td>
                                                <td className="px-4 py-3 text-sm text-mld-text-secondary">
                                                    {canViewProviderConsole ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleViewProviderConsole(provider.id, {
                                                                orgId: selectedOrg?.id ?? null,
                                                                subdomain: selectedOrg?.subdomain ?? subdomain,
                                                                returnState: selectedOrg?.id ? {orgId: selectedOrg.id, orgDetailTab: activeTab, orgFilter: orgFilter ?? ''} : undefined,
                                                            })}
                                                            disabled={providerSwitching === provider.id}
                                                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-mld-primary px-3 py-1.5 text-xs font-semibold text-mld-primary hover:bg-mld-primary/10 disabled:opacity-60">
                                                            {providerSwitching === provider.id ? 'Opening...' : 'View Console'}
                                                        </button>
                                                    ) : (
                                                        '—'
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-mld-text-secondary">{formatBool(provider.is_active)}</td>
                                                {MODULE_KEYS.map((moduleKey) => (
                                                    <td key={moduleKey} className="px-4 py-3 text-center">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                handleProviderModuleToggle(
                                                                    provider.id,
                                                                    moduleKey,
                                                                    !moduleMap[moduleKey]
                                                                )
                                                            }
                                                            disabled={!orgModules[moduleKey]}
                                                            aria-pressed={!!moduleMap[moduleKey]}
                                                            title={
                                                                orgModules[moduleKey]
                                                                    ? 'Toggle provider module'
                                                                    : 'Enable this module at the organization level first.'
                                                            }
                                                            className={clsx(
                                                                'inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold transition',
                                                                moduleMap[moduleKey]
                                                                    ? 'bg-mld-primary/10 text-mld-primary hover:bg-mld-primary/20'
                                                                    : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border',
                                                                !orgModules[moduleKey] && 'cursor-not-allowed opacity-60'
                                                            )}>
                                                            {moduleMap[moduleKey] ? 'On' : 'Off'}
                                                        </button>
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {sortedProviders.length === 0 ? (
                                <div className="px-4 py-6 text-sm text-mld-muted-text">No providers match this filter.</div>
                            ) : null}
                        </div>
                        {sortedProviders.length > 0 ? (
                            <div className="mt-4 flex flex-col gap-3 text-sm text-mld-text-secondary sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    Showing {pageStart}-{pageEnd} of {totalProviders}
                                    {inactiveCount > 0 && (
                                        <>
                                            {' · '}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowInactive((prev) => !prev);
                                                    setProviderPage(1);
                                                }}
                                                className="text-mld-primary underline hover:text-mld-primary">
                                                {showInactive ? 'Hide inactive' : `Show inactive (${inactiveCount})`}
                                            </button>
                                        </>
                                    )}
                                </div>
                                <div className="flex items-center gap-3">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setProviderPage((prev) => Math.max(1, prev - 1))}
                                        disabled={currentPage <= 1}>
                                        Previous
                                    </Button>
                                    <span className="text-xs text-mld-muted-text">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => setProviderPage((prev) => Math.min(totalPages, prev + 1))}
                                        disabled={currentPage >= totalPages}>
                                        Next
                                    </Button>
                                </div>
                            </div>
                        ) : null}
                    </Card.Content>
                </Card>
            )}

            {showProvidersSection && selectedOrg && providers.length > 0 && patientOrgModules && (
                <Card>
                    <Card.Header>
                        <Card.Title>Patient Dashboard Access</Card.Title>
                        <p className="text-sm text-mld-muted-text">
                            Per-provider switches for patient dashboard cards. A patient sees a module when the
                            organization allows it and any of their providers has it on (providers with no explicit
                            setting count as on). Rows follow the provider list and pagination above.
                        </p>
                    </Card.Header>
                    <Card.Content>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-mld-panel-border">
                                <thead className="bg-mld-menu-background">
                                    <tr>
                                        <th
                                            aria-sort={
                                                providerSort.key === 'name'
                                                    ? providerSort.direction === 'asc'
                                                        ? 'ascending'
                                                        : 'descending'
                                                    : 'none'
                                            }
                                            className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                            <button
                                                type="button"
                                                onClick={() => handleProviderSort('name')}
                                                className="group inline-flex w-full items-center gap-2">
                                                Provider
                                                {providerSortIndicator('name')}
                                            </button>
                                        </th>
                                        {PATIENT_MODULE_KEYS.map((moduleKey) => (
                                            <th
                                                key={moduleKey}
                                                className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                <button
                                                    type="button"
                                                    onClick={() => handleProviderSort(moduleKey)}
                                                    className="group inline-flex w-full items-center justify-center gap-2">
                                                    {PATIENT_MODULE_LABELS[moduleKey]}
                                                    {providerSortIndicator(moduleKey)}
                                                </button>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-mld-panel-border bg-white">
                                    {paginatedProviders.map((provider) => {
                                        const patientMap = patientProviderModules[provider.id] ?? {};
                                        return (
                                            <tr
                                                key={provider.id}
                                                className={`hover:bg-mld-menu-background${provider.is_active === false ? ' opacity-50' : ''}`}>
                                                <td className="px-4 py-3 text-sm font-medium text-mld-text-primary">{provider.name}</td>
                                                {PATIENT_MODULE_KEYS.map((moduleKey) => {
                                                    const orgEnabled = !!patientOrgModules[moduleKey];
                                                    const explicit = patientMap[moduleKey];
                                                    // No explicit row = default on; the org switch caps it.
                                                    const effective = orgEnabled && (explicit ?? true);
                                                    return (
                                                        <td key={moduleKey} className="px-4 py-3 text-center">
                                                            <button
                                                                type="button"
                                                                onClick={() =>
                                                                    handlePatientProviderModuleToggle(
                                                                        provider.id,
                                                                        moduleKey,
                                                                        !(explicit ?? true)
                                                                    )
                                                                }
                                                                disabled={!orgEnabled}
                                                                aria-pressed={effective}
                                                                title={
                                                                    orgEnabled
                                                                        ? 'Toggle this module for the patients of this provider'
                                                                        : 'Enable this module at the organization level first.'
                                                                }
                                                                className={clsx(
                                                                    'inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold transition',
                                                                    effective
                                                                        ? 'bg-mld-primary/10 text-mld-primary hover:bg-mld-primary/20'
                                                                        : 'bg-mld-menu-background text-mld-muted-text hover:bg-mld-panel-border',
                                                                    !orgEnabled && 'cursor-not-allowed opacity-60'
                                                                )}>
                                                                {effective ? 'On' : 'Off'}
                                                            </button>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {sortedProviders.length === 0 ? (
                                <div className="px-4 py-6 text-sm text-mld-muted-text">No providers match this filter.</div>
                            ) : null}
                        </div>
                    </Card.Content>
                </Card>
            )}

            {showSubdomainSection && selectedOrg && orgModules && (
                <Card>
                    <Card.Header className="flex items-center justify-between">
                        <Card.Title>Subdomain &amp; Logo</Card.Title>
                        {subdomain && baseDomain ? (
                            <span className="text-xs font-medium text-mld-muted-text">{subdomain}.{baseDomain}</span>
                        ) : null}
                    </Card.Header>
                    <Card.Content>
                        <div className="flex flex-col gap-6 md:flex-row md:items-start">
                            {/* Logo box */}
                            <div className="flex flex-col items-center gap-2">
                                {logoUrl ? (
                                    <button
                                        type="button"
                                        onClick={() => setLogoModalOpen(true)}
                                        className="group relative flex h-[100px] w-[100px] items-center justify-center overflow-hidden rounded-lg border border-mld-panel-border bg-mld-menu-background">
                                        <img
                                            src={resolveMediaUrl(logoUrl)}
                                            alt={`${selectedOrg.display_name || selectedOrg.name} logo`}
                                            className="max-h-full max-w-full object-contain p-2"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                                            <span className="text-xs font-medium text-white">Change</span>
                                        </div>
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => setLogoModalOpen(true)}
                                        className="flex h-[100px] w-[100px] flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-mld-panel-border bg-mld-menu-background text-mld-muted-text transition-colors hover:border-mld-primary hover:text-mld-primary">
                                        <ImageIcon className="h-6 w-6" />
                                        <span className="text-xs font-medium">Add Logo</span>
                                    </button>
                                )}
                                {logoUrl ? (
                                    <button
                                        type="button"
                                        onClick={handleLogoDelete}
                                        disabled={logoBusy}
                                        className="text-xs text-mld-danger hover:text-mld-danger disabled:opacity-60">
                                        Remove logo
                                    </button>
                                ) : null}
                                {logoMessage.text ? (
                                    <div
                                        className={`mt-1 rounded-md px-2 py-1 text-xs ${
                                            logoMessage.type === 'success'
                                                ? 'bg-mld-primary/10 text-mld-primary'
                                                : 'bg-mld-danger/5 text-mld-danger'
                                        }`}>
                                        {logoMessage.text}
                                    </div>
                                ) : null}
                            </div>

                            {/* Subdomain */}
                            <div className="flex-1">
                                <label className="mb-2 block text-sm font-medium text-mld-text-secondary">Subdomain</label>
                                <div className="flex items-center gap-2">
                                    <div className="flex max-w-sm items-center overflow-hidden rounded-md border border-mld-panel-border focus-within:border-transparent focus-within:ring-2 focus-within:ring-mld-primary">
                                        <span className="whitespace-nowrap bg-mld-menu-background px-3 py-2 text-sm text-mld-muted-text">https://</span>
                                        <input
                                            type="text"
                                            value={subdomain}
                                            onChange={handleSubdomainChange}
                                            className="min-w-0 flex-1 border-0 px-2 py-2 text-sm focus:outline-none focus:ring-0"
                                        />
                                        <span className="whitespace-nowrap bg-mld-menu-background px-3 py-2 text-sm text-mld-muted-text">.{baseDomain}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleSubdomainSave}
                                        disabled={isSubdomainBusy || !isSubdomainDirty}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                        {isSubdomainBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                        Save
                                    </button>
                                </div>
                                {subdomain && baseDomain ? (
                                    <div className="mt-2 text-xs text-mld-muted-text">
                                        <button
                                            type="button"
                                            onClick={handleCopyUrl}
                                            className="text-mld-primary hover:text-mld-primary-700">
                                            Copy URL
                                        </button>
                                    </div>
                                ) : null}
                                {subdomainMessage.text ? (
                                    <div
                                        className={`mt-2 rounded-md px-3 py-2 text-sm ${
                                            subdomainMessage.type === 'success'
                                                ? 'bg-mld-primary/10 text-mld-primary'
                                                : 'bg-mld-danger/5 text-mld-danger'
                                        }`}>
                                        {subdomainMessage.text}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </Card.Content>
                </Card>
            )}

            {/* Brand Theme — per-org white-labeling (STYLE_GUIDE §2) */}
            {showSubdomainSection && selectedOrg && (
                <Card>
                    <Card.Header className="flex items-center justify-between">
                        <Card.Title>Brand Theme</Card.Title>
                        {isCustomCssDirty ? (
                            <span className="text-xs font-medium text-mld-warning">Unsaved changes</span>
                        ) : null}
                    </Card.Header>
                    <Card.Content>
                        <p className="mb-4 text-xs text-mld-muted-text">
                            Override the MLD brand for this organization. Each picker maps to a CSS variable that
                            propagates to every component using that token (buttons, borders, headings, etc.).
                            Loaded on bootstrap for any user whose tenant resolves to this org. Leave a row at its
                            default to keep the MLD brand color.
                        </p>

                        <div className="divide-y divide-mld-panel-border/60 rounded-lg border border-mld-panel-border">
                            {BRAND_TOKENS.map((token) => {
                                // Effective default may differ from the static defaultHex for
                                // surface/text tokens once the tenant overrides --mld-background:
                                // those auto-derive at runtime, and the picker shows that derived
                                // shade so the design and the painted UI agree.
                                const effective = effectiveDefaultHex(token.name, customCssTokens);
                                const current = customCssTokens[token.name] ?? effective;
                                const isOverridden =
                                    !!customCssTokens[token.name] &&
                                    customCssTokens[token.name].toUpperCase() !== effective.toUpperCase();
                                return (
                                    <div
                                        key={token.name}
                                        className="grid grid-cols-[1fr_auto] items-center gap-4 px-4 py-3">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium text-mld-text-primary">
                                                    {token.label}
                                                </span>
                                                <code className="rounded bg-mld-menu-background px-1.5 py-0.5 font-mono text-[10px] text-mld-text-secondary">
                                                    --{token.name}
                                                </code>
                                                {isOverridden ? (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleCustomCssTokenReset(token.name)}
                                                        className="text-[11px] font-medium text-mld-primary hover:underline">
                                                        Reset to default
                                                    </button>
                                                ) : null}
                                            </div>
                                            <p className="mt-0.5 text-xs text-mld-muted-text">{token.hint}</p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={current}
                                                onChange={(e) =>
                                                    handleCustomCssTokenChange(token.name, e.target.value)
                                                }
                                                className="h-9 w-12 cursor-pointer rounded border border-mld-panel-border bg-white p-0.5"
                                                aria-label={`${token.label} color`}
                                            />
                                            <code className="font-mono text-xs uppercase text-mld-text-secondary">
                                                {current}
                                            </code>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-5">
                            <label className="mb-1 block text-sm font-medium text-mld-text-primary">
                                Additional CSS{' '}
                                <span className="font-normal text-mld-muted-text">(optional)</span>
                            </label>
                            <p className="mb-2 text-xs text-mld-muted-text">
                                Anything that isn't a brand color override — custom fonts, one-off rules, status-color
                                overrides. Sanitized server-side (no{' '}
                                <code className="rounded bg-mld-menu-background px-1 py-0.5 font-mono text-[11px]">@import</code>,
                                external URLs, or{' '}
                                <code className="rounded bg-mld-menu-background px-1 py-0.5 font-mono text-[11px]">expression()</code>).
                                Max 50KB total.
                            </p>
                            <textarea
                                value={customCssExtras}
                                onChange={handleCustomCssExtrasChange}
                                spellCheck={false}
                                placeholder={`body {\n  font-family: 'Sahar', sans-serif;\n}`}
                                rows={6}
                                className="w-full rounded-lg border border-mld-panel-border px-3 py-2 font-mono text-xs leading-relaxed focus:outline-none focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                            />
                        </div>

                        <div className="mt-4 flex items-center gap-3">
                            <Button
                                type="button"
                                onClick={handleCustomCssSave}
                                loading={customCssMutation.isPending}
                                disabled={!isCustomCssDirty}>
                                Save
                            </Button>
                            {customCssOriginal.trim() ? (
                                <Button
                                    variant="secondary"
                                    size="md"
                                    onClick={handleCustomCssClear}
                                    disabled={customCssMutation.isPending}>
                                    Clear all overrides
                                </Button>
                            ) : null}
                            {customCssMessage.text ? (
                                <span
                                    className={`text-xs ${
                                        customCssMessage.type === 'success' ? 'text-mld-primary' : 'text-mld-danger'
                                    }`}>
                                    {customCssMessage.text}
                                </span>
                            ) : null}
                        </div>
                        <p className="mt-3 text-[11px] text-mld-muted-text">
                            Tip: open this org's subdomain in another tab and hard-refresh after saving to see the
                            new theme apply.
                        </p>
                    </Card.Content>
                </Card>
            )}

            {/* Logo Modal */}
            <Dialog open={logoModalOpen} onClose={handleLogoModalClose} className="relative z-50">
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md rounded-lg bg-white shadow-xl">
                        <div className="flex items-center justify-between border-b px-5 py-4">
                            <Dialog.Title className="text-base font-semibold text-mld-text-primary">Organization Logo</Dialog.Title>
                            <button type="button" onClick={handleLogoModalClose} className="text-mld-muted-text hover:text-mld-text-secondary">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="px-5 py-4">
                            {/* Drop zone */}
                            <div
                                onDragOver={(e) => {
                                    e.preventDefault();
                                    setLogoModalDragOver(true);
                                }}
                                onDragLeave={() => setLogoModalDragOver(false)}
                                onDrop={handleLogoModalDrop}
                                className={clsx(
                                    'flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-4 py-8 transition-colors',
                                    logoModalDragOver
                                        ? 'border-mld-primary bg-mld-success/5'
                                        : 'border-mld-panel-border bg-mld-menu-background'
                                )}>
                                <Upload className="mb-2 h-8 w-8 text-mld-muted-text" />
                                <p className="text-sm text-mld-text-secondary">
                                    <button
                                        type="button"
                                        onClick={() => logoModalFileRef.current?.click()}
                                        className="font-medium text-mld-primary hover:text-mld-primary-700">
                                        Choose a file
                                    </button>
                                    {' '}or drag it here
                                </p>
                                <p className="mt-1 text-xs text-mld-muted-text">PNG, JPG, SVG &middot; Max 2 MB</p>
                                <input
                                    ref={logoModalFileRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                                    onChange={handleLogoModalFileSelect}
                                    className="hidden"
                                />
                            </div>

                            {/* Preview */}
                            {logoModalPreview ? (
                                <div className="mt-4 flex items-center gap-3 rounded-md border border-mld-panel-border bg-mld-menu-background p-3">
                                    <img src={logoModalPreview} alt="Preview" className="h-12 w-12 rounded object-contain" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm text-mld-text-secondary">{logoModalFile?.name}</p>
                                        <p className="text-xs text-mld-muted-text">
                                            {logoModalFile ? `${(logoModalFile.size / 1024).toFixed(1)} KB` : ''}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (logoModalPreview) URL.revokeObjectURL(logoModalPreview);
                                            setLogoModalFile(null);
                                            setLogoModalPreview(null);
                                        }}
                                        className="text-mld-muted-text hover:text-mld-text-secondary">
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : null}

                            {/* Divider */}
                            <div className="my-4 flex items-center gap-3">
                                <div className="h-px flex-1 bg-mld-panel-border" />
                                <span className="text-xs font-medium text-mld-muted-text">OR</span>
                                <div className="h-px flex-1 bg-mld-panel-border" />
                            </div>

                            {/* URL input */}
                            <label className="mb-1.5 block text-sm font-medium text-mld-text-secondary">Paste logo URL</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={logoUrlInput}
                                    onChange={handleLogoUrlInputChange}
                                    placeholder="https://example.com/logo.png"
                                    className="min-w-0 flex-1 rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                    disabled={logoBusy}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleLogoUrlSave();
                                    }}
                                    disabled={logoBusy || !logoUrlInput.trim()}
                                    className="inline-flex items-center rounded-md bg-mld-primary px-3 py-2 text-sm font-medium text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                    Save URL
                                </button>
                            </div>
                        </div>
                        <div className="flex justify-end gap-3 border-t px-5 py-4">
                            <button
                                type="button"
                                onClick={handleLogoModalClose}
                                className="rounded-md border border-mld-panel-border px-4 py-2 text-sm font-medium text-mld-text-secondary hover:bg-mld-menu-background">
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleLogoModalUpload}
                                disabled={!logoModalFile || logoBusy}
                                className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm font-medium text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                {logoUploadMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                Upload Logo
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>

            {showLoginsSection && selectedOrg && (
                <Card>
                    <Card.Header>
                        <Card.Title>Organization Logins</Card.Title>
                    </Card.Header>
                    <Card.Content>
                        <div className="space-y-6">
                            <div>
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                                    <h4 className="text-sm font-semibold text-mld-text-secondary">Organization Users</h4>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={handleToggleOrgAdd}
                                            iconLeft={<Plus className="h-4 w-4" />}>
                                            {orgAddOpen ? 'Hide' : 'Add Organization User'}
                                        </Button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setOrgNameEditing(true);
                                                setOrgNameDraft(selectedOrg?.name ?? '');
                                            }}
                                            className="rounded-md border border-mld-panel-border px-2 py-1 text-xs text-mld-text-secondary hover:bg-mld-menu-background">
                                            Edit Organization Name
                                        </button>
                                    </div>
                                </div>
                                {orgNameEditing && (
                                    <div className="mb-4 flex items-center gap-2 rounded-md border border-mld-info/20 bg-mld-info/5 px-3 py-2">
                                        <label className="text-xs font-medium text-mld-text-secondary">Name:</label>
                                        <input
                                            type="text"
                                            value={orgNameDraft}
                                            onChange={(e) => setOrgNameDraft(e.target.value)}
                                            className="flex-1 rounded border border-gray-300 px-2 py-1 text-xs"
                                        />
                                        <button
                                            type="button"
                                            disabled={orgNameSaving || !orgNameDraft.trim() || orgNameDraft.trim() === selectedOrg?.name}
                                            onClick={async () => {
                                                if (!selectedOrg) return;
                                                setOrgNameSaving(true);
                                                try {
                                                    await AdminService.updateOrganizationName(selectedOrg.id, orgNameDraft.trim());
                                                    await queryClient.refetchQueries({queryKey: ['admin-organizations']});
                                                    setOrgNameEditing(false);
                                                } catch (err) {
                                                    console.error('Failed to update org name', err);
                                                } finally {
                                                    setOrgNameSaving(false);
                                                }
                                            }}
                                            className="rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
                                            {orgNameSaving ? 'Saving…' : 'Save'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setOrgNameEditing(false)}
                                            className="rounded px-2 py-1 text-xs text-mld-muted-text hover:bg-mld-menu-background">
                                            Cancel
                                        </button>
                                    </div>
                                )}
                                {organizationUsers.length === 0 ? (
                                    <p className="text-sm text-mld-muted-text">No organization users found.</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-mld-panel-border">
                                            <thead className="bg-mld-menu-background">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                        Name
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                        Email
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                        Role
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                        Verified
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                        Actions
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-mld-panel-border bg-white">
                                                {organizationUsers.map((user) => {
                                                    const isEditing = orgUserEdit?.id === user.id;
                                                    return (
                                                        <React.Fragment key={user.id}>
                                                            <tr className="hover:bg-mld-menu-background">
                                                                <td className="px-4 py-2 text-sm text-mld-text-primary">
                                                                    {user.first_name} {user.last_name}
                                                                </td>
                                                                <td className="px-4 py-2 text-sm text-mld-text-secondary">{user.email}</td>
                                                                <td className="px-4 py-2 text-sm text-mld-text-secondary">
                                                                    <span className="font-medium text-mld-text-secondary">
                                                                        {roleLabel(user.role_id)}
                                                                    </span>
                                                                    <span className="ml-2 text-xs text-mld-muted-text">
                                                                        #{user.role_id ?? '—'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-2 text-sm text-mld-text-secondary">
                                                                    {formatBool(user.verified)}
                                                                </td>
                                                                <td className="px-4 py-2 text-sm text-mld-text-secondary">
                                                                    <div className="flex items-center gap-3">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleEditOrgUser(user)}
                                                                            className="text-sm font-medium text-mld-primary hover:text-mld-primary-700">
                                                                            Edit
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                if (selectedOrg && window.confirm(`Delete ${user.first_name} ${user.last_name}?`)) {
                                                                                    deleteOrgUserMutation.mutate({orgId: selectedOrg.id, userId: user.id});
                                                                                }
                                                                            }}
                                                                            disabled={deleteOrgUserMutation.isPending}
                                                                            className="text-sm font-medium text-mld-danger hover:text-mld-danger disabled:opacity-50">
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            {isEditing ? (
                                                                <tr className="bg-mld-menu-background">
                                                                    <td colSpan={5} className="px-4 py-3">
                                                                        <div className="rounded-md border border-mld-panel-border bg-white p-4">
                                                                            <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                                                                                <input
                                                                                    type="text"
                                                                                    value={orgUserEdit?.form.first_name ?? ''}
                                                                                    onChange={(event) =>
                                                                                        handleOrgUserEditChange(
                                                                                            'first_name',
                                                                                            event.target.value
                                                                                        )
                                                                                    }
                                                                                    placeholder="First name"
                                                                                    className={
                                                                                        orgUserEditFieldErrors.first_name
                                                                                            ? INPUT_ERROR_CLASS
                                                                                            : INPUT_DEFAULT_CLASS
                                                                                    }
                                                                                />
                                                                                <input
                                                                                    type="text"
                                                                                    value={orgUserEdit?.form.last_name ?? ''}
                                                                                    onChange={(event) =>
                                                                                        handleOrgUserEditChange(
                                                                                            'last_name',
                                                                                            event.target.value
                                                                                        )
                                                                                    }
                                                                                    placeholder="Last name"
                                                                                    className={
                                                                                        orgUserEditFieldErrors.last_name
                                                                                            ? INPUT_ERROR_CLASS
                                                                                            : INPUT_DEFAULT_CLASS
                                                                                    }
                                                                                />
                                                                                <input
                                                                                    type="email"
                                                                                    value={orgUserEdit?.form.email ?? ''}
                                                                                    onChange={(event) =>
                                                                                        handleOrgUserEditChange('email', event.target.value)
                                                                                    }
                                                                                    placeholder="Email"
                                                                                    className={
                                                                                        orgUserEditFieldErrors.email
                                                                                            ? INPUT_ERROR_CLASS
                                                                                            : INPUT_DEFAULT_CLASS
                                                                                    }
                                                                                />
                                                                                <input
                                                                                    type="text"
                                                                                    value={orgUserEdit?.form.phone ?? ''}
                                                                                    onChange={(event) =>
                                                                                        handleOrgUserEditChange('phone', event.target.value)
                                                                                    }
                                                                                    placeholder="Phone"
                                                                                    className={
                                                                                        orgUserEditFieldErrors.phone
                                                                                            ? INPUT_ERROR_CLASS
                                                                                            : INPUT_DEFAULT_CLASS
                                                                                    }
                                                                                />
                                                                            </div>
                                                                            <div className="mt-3 flex flex-wrap items-center gap-3">
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={handleSaveOrgUserEdit}
                                                                                    disabled={updateOrgUserMutation.isPending}
                                                                                    className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-3 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                                                                    {updateOrgUserMutation.isPending ? (
                                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                                    ) : null}
                                                                                    Save Changes
                                                                                </button>
                                                                                <Button
                                                                                    variant="secondary"
                                                                                    size="md"
                                                                                    onClick={handleCancelOrgUserEdit}>
                                                                                    Cancel
                                                                                </Button>
                                                                                {orgUserEditMessage.text ? (
                                                                                    <span
                                                                                        className={`text-sm ${
                                                                                            orgUserEditMessage.type === 'success'
                                                                                                ? 'text-mld-primary'
                                                                                                : 'text-mld-danger'
                                                                                        }`}>
                                                                                        {orgUserEditMessage.text}
                                                                                    </span>
                                                                                ) : null}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ) : null}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {orgAddOpen ? (
                                    <div className="mt-4 rounded-md border border-mld-panel-border bg-mld-menu-background px-4 py-4">
                                        <div className="mb-3 flex items-center justify-between">
                                            <p className="text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                                New organization user
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 md:grid-cols-6 md:items-center">
                                            <input
                                                type="text"
                                                value={orgUserForm.first_name}
                                                onChange={(event) => handleOrgUserChange('first_name', event.target.value)}
                                                placeholder="First name"
                                                    className={
                                                        orgUserFieldErrors.first_name ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS
                                                    }
                                            />
                                            <input
                                                type="text"
                                                value={orgUserForm.last_name}
                                                onChange={(event) => handleOrgUserChange('last_name', event.target.value)}
                                                placeholder="Last name"
                                                    className={
                                                        orgUserFieldErrors.last_name ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS
                                                    }
                                            />
                                            <input
                                                type="email"
                                                value={orgUserForm.email}
                                                onChange={(event) => handleOrgUserChange('email', event.target.value)}
                                                placeholder="Email"
                                                    className={
                                                        orgUserFieldErrors.email ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS
                                                    }
                                            />
                                            <input
                                                type="text"
                                                value={orgUserForm.phone}
                                                onChange={(event) => handleOrgUserChange('phone', event.target.value)}
                                                placeholder="Phone"
                                                    className={
                                                        orgUserFieldErrors.phone ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS
                                                    }
                                            />
                                            <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                                                <button
                                                    type="button"
                                                    onClick={handleCreateOrgUser}
                                                    disabled={createOrgUserMutation.isPending}
                                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-3 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                                    {createOrgUserMutation.isPending ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : null}
                                                    Create Org User
                                                </button>
                                                <Button
                                                    variant="secondary"
                                                    size="md"
                                                    onClick={handleToggleOrgAdd}>
                                                    Cancel
                                                </Button>
                                            </div>
                                        </div>
                                        {orgUserMessage.text ? (
                                            <div
                                                className={`mt-3 text-sm ${
                                                    orgUserMessage.type === 'success'
                                                        ? 'text-mld-primary'
                                                        : 'text-mld-danger'
                                                }`}>
                                                {orgUserMessage.text}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                            <div>
                                <div className="mb-3 flex items-center justify-between">
                                    <h4 className="text-sm font-semibold text-mld-text-secondary">Provider Users</h4>
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => {
                                            setAddProviderOpen((prev) => !prev);
                                            setAddProviderMessage({type: '', text: ''});
                                            if (!addProviderOpen) {
                                                setAddProviderName('');
                                                setAddProviderNpi('');
                                            }
                                        }}
                                        iconLeft={<Plus className="h-3 w-3" />}>
                                        {addProviderOpen ? 'Hide' : 'Add Provider'}
                                    </Button>
                                </div>
                                {addProviderMessage.text && !addProviderOpen ? (
                                    <div className={`mb-3 text-sm ${addProviderMessage.type === 'success' ? 'text-mld-primary' : 'text-mld-danger'}`}>
                                        {addProviderMessage.text}
                                    </div>
                                ) : null}
                                {deleteProviderMessage.text && !deleteProviderOpen ? (
                                    <div className={`mb-3 text-sm ${deleteProviderMessage.type === 'success' ? 'text-mld-primary' : 'text-mld-danger'}`}>
                                        {deleteProviderMessage.text}
                                    </div>
                                ) : null}
                                {deleteProviderOpen ? (
                                    <div className="mb-4 rounded-md border border-mld-danger/20 bg-mld-danger/5 px-4 py-4">
                                        <p className="mb-1 text-sm font-semibold text-mld-danger">Delete {deleteProviderName}</p>
                                        <p className="mb-3 text-xs text-mld-danger">
                                            All patients, orders, and data will be reassigned to the selected provider. This cannot be undone.
                                        </p>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <label className="text-xs font-semibold text-mld-text-secondary whitespace-nowrap">Transfer data to:</label>
                                            <select
                                                value={deleteProviderTarget ?? ''}
                                                onChange={(event) => setDeleteProviderTarget(event.target.value ? Number(event.target.value) : null)}
                                                className={`min-w-[200px] rounded-md border border-gray-300 px-3 py-2 pr-8 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary ${deleteProviderTarget == null ? 'text-mld-muted-text' : 'text-mld-text-primary'}`}>
                                                <option value="" disabled>Select provider</option>
                                                {providers
                                                    .filter((p) => p.id !== deleteProviderId && p.is_active !== false)
                                                    .map((p) => (
                                                        <option key={p.id} value={p.id}>
                                                            {p.name}
                                                        </option>
                                                    ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={handleConfirmDeleteProvider}
                                                disabled={!deleteProviderTarget || deleteProviderMutation.isPending}
                                                className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm disabled:opacity-50 ${deleteProviderTarget ? 'border-mld-danger bg-mld-danger text-white hover:bg-mld-danger/80' : 'border-mld-danger/30 bg-transparent text-mld-danger/60'}`}>
                                                {deleteProviderMutation.isPending ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : null}
                                                Delete {deleteProviderName}
                                            </button>
                                            <Button
                                                variant="secondary"
                                                size="md"
                                                onClick={handleCancelDeleteProvider}>
                                                Cancel
                                            </Button>
                                        </div>
                                        {deleteProviderMessage.text ? (
                                            <div className={`mt-3 text-sm ${deleteProviderMessage.type === 'success' ? 'text-mld-primary' : 'text-mld-danger'}`}>
                                                {deleteProviderMessage.text}
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                                {providers.length === 0 ? (
                                    <p className="text-sm text-mld-muted-text">No providers found for this organization.</p>
                                ) : (
                                    <div className="space-y-4">
                                        {providerUserGroups.map((group) => {
                                            const isOpen = providerGroupsOpen[group.providerId] ?? true;
                                            const isEditingName = providerNameEditing[group.providerId] ?? false;
                                            const draftName = providerNameDrafts[group.providerId] ?? group.providerName;
                                            const trimmedDraft = draftName.trim();
                                            const trimmedCurrent = group.providerName.trim();
                                            const isNameDirty = trimmedDraft !== trimmedCurrent;
                                            const isSavingName =
                                                providerNameSaving === group.providerId && updateProviderNameMutation.isPending;
                                            const providerErrors = providerUserFieldErrors[group.providerId] ?? {};
                                            const disableSave = !isNameDirty || !trimmedDraft || isSavingName;
                                            return (
                                                <div key={group.providerId} className="rounded-md border border-mld-panel-border">
                                                    <div className="flex w-full items-center justify-between gap-4 px-4 py-3 text-sm font-semibold text-mld-text-primary hover:bg-mld-menu-background">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleToggleProviderGroup(group.providerId)}
                                                            className="flex min-w-0 flex-1 items-center gap-3 text-left">
                                                            {isEditingName ? (
                                                                <input
                                                                    type="text"
                                                                    value={draftName}
                                                                    onChange={(event) =>
                                                                        handleProviderNameChange(group.providerId, event.target.value)
                                                                    }
                                                                    onClick={(event) => event.stopPropagation()}
                                                                    className="w-full rounded-md border border-mld-panel-border px-2 py-1 text-sm font-medium text-mld-text-primary focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                />
                                                            ) : (
                                                                <span className="truncate">{group.providerName}</span>
                                                            )}
                                                        </button>
                                                        <span className="flex flex-wrap items-center gap-2 text-xs text-mld-muted-text">
                                                            <span>
                                                                {group.users.length} user{group.users.length === 1 ? '' : 's'}
                                                            </span>
                                                            <Button
                                                                variant="secondary"
                                                                size="sm"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    handleToggleProviderAdd(group.providerId);
                                                                }}
                                                                iconLeft={<Plus className="h-3 w-3" />}>
                                                                {providerAddOpen[group.providerId] ? 'Hide' : 'Add Provider User'}
                                                            </Button>
                                                            {isEditingName ? (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="rounded-md border border-mld-panel-border px-2 py-1 text-xs text-mld-text-secondary hover:bg-mld-menu-background disabled:opacity-60"
                                                                        onClick={() =>
                                                                            handleCancelProviderName(group.providerId, group.providerName)
                                                                        }
                                                                        disabled={isSavingName}>
                                                                        Cancel
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        className={`rounded-md border border-mld-panel-border px-2 py-1 text-xs ${
                                                                            disableSave
                                                                                ? 'text-mld-muted-text'
                                                                                : 'text-mld-text-secondary hover:bg-mld-menu-background'
                                                                        }`}
                                                                        onClick={() => handleSaveProviderName(group.providerId)}
                                                                        disabled={disableSave}>
                                                                        {isSavingName ? (
                                                                            <Loader2 className="mr-1 inline-flex h-3 w-3 animate-spin" />
                                                                        ) : null}
                                                                        Save Name
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        className="rounded-md border border-mld-panel-border px-2 py-1 text-xs text-mld-text-secondary hover:bg-mld-menu-background"
                                                                        onClick={() => handleEditProviderName(group.providerId, group.providerName)}>
                                                                        Edit Provider Name
                                                                    </button>
                                                                    {providers.length >= 2 ? (
                                                                        <button
                                                                            type="button"
                                                                            className="inline-flex items-center gap-1 rounded-md border border-mld-danger/30 px-2 py-1 text-xs text-mld-danger hover:bg-mld-danger/5"
                                                                            onClick={(event) => {
                                                                                event.stopPropagation();
                                                                                handleOpenDeleteProvider(group.providerId, group.providerName);
                                                                            }}>
                                                                            <Trash2 className="h-3 w-3" />
                                                                            Delete
                                                                        </button>
                                                                    ) : null}
                                                                </>
                                                            )}
                                                        </span>
                                                    </div>
                                                    {isOpen ? (
                                                        <div className="overflow-x-auto">
                                                            <table className="min-w-full divide-y divide-mld-panel-border">
                                                                <thead className="bg-mld-menu-background">
                                                                    <tr>
                                                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                                            Name
                                                                        </th>
                                                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                                            Email
                                                                        </th>
                                                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                                            Provider Roles
                                                                        </th>
                                                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                                            Verified
                                                                        </th>
                                                                        <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-mld-muted-text">
                                                                            Actions
                                                                        </th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y divide-mld-panel-border bg-white">
                                                                    {group.users.map((user) => {
                                                                        const isEditing =
                                                                            providerUserEdit?.id === user.id &&
                                                                            providerUserEdit?.providerId === group.providerId;
                                                                        return (
                                                                            <React.Fragment key={`${group.providerId}-${user.id}`}>
                                                                                <tr className="hover:bg-mld-menu-background">
                                                                                    <td className="px-4 py-2 text-sm text-mld-text-primary">
                                                                                        {user.first_name} {user.last_name}
                                                                                    </td>
                                                                                    <td className="px-4 py-2 text-sm text-mld-text-secondary">
                                                                                        {user.email}
                                                                                    </td>
                                                                                    <td className="px-4 py-2 text-sm text-mld-text-secondary">
                                                                                        <div className="text-sm text-mld-text-secondary">
                                                                                            {providerRoleDisplay(group.providerId, user.id)}
                                                                                        </div>
                                                                                        <div className="text-xs text-mld-muted-text">
                                                                                            System: {roleLabel(user.role_id)} #{user.role_id ?? '—'}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-4 py-2 text-sm text-mld-text-secondary">
                                                                                        {formatBool(user.verified)}
                                                                                    </td>
                                                                                    <td className="px-4 py-2 text-sm text-mld-text-secondary">
                                                                                        <div className="flex items-center gap-2">
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => handleEditProviderUser(user)}
                                                                                                className="text-sm font-medium text-mld-primary hover:text-mld-primary-700">
                                                                                                Edit
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => handleDeleteProviderUser(user)}
                                                                                                disabled={
                                                                                                    deleteProviderUserMutation.isPending &&
                                                                                                    deleteProviderUserMutation.variables?.userId === user.id
                                                                                                }
                                                                                                className="text-sm font-medium text-mld-danger hover:text-mld-danger disabled:opacity-50">
                                                                                                {deleteProviderUserMutation.isPending &&
                                                                                                deleteProviderUserMutation.variables?.userId === user.id
                                                                                                    ? 'Removing…'
                                                                                                    : 'Delete'}
                                                                                            </button>
                                                                                            {canViewProviderConsole ? (
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() =>
                                                                                                        handleViewProviderConsole(group.providerId, {
                                                                                                            orgId: selectedOrg?.id ?? null,
                                                                                                            subdomain: selectedOrg?.subdomain ?? subdomain,
                                                                                                            returnState: selectedOrg?.id ? {orgId: selectedOrg.id, orgDetailTab: activeTab, orgFilter: orgFilter ?? ''} : undefined,
                                                                                                        })
                                                                                                    }
                                                                                                    disabled={providerSwitching === group.providerId}
                                                                                                    className="inline-flex items-center whitespace-nowrap rounded-md border border-mld-primary px-2 py-1 text-xs font-semibold text-mld-primary hover:bg-mld-primary/10 disabled:opacity-60">
                                                                                                    {providerSwitching === group.providerId
                                                                                                        ? 'Opening...'
                                                                                                        : 'View Console'}
                                                                                                </button>
                                                                                            ) : null}
                                                                                        </div>
                                                                                    </td>
                                                                                </tr>
                                                                                {isEditing ? (
                                                                                    <tr className="bg-mld-menu-background">
                                                                                        <td colSpan={5} className="px-4 py-3">
                                                                                            <div className="rounded-md border border-mld-panel-border bg-white p-4">
                                                                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        value={providerUserEdit?.form.first_name ?? ''}
                                                                                                        onChange={(event) =>
                                                                                                            handleProviderUserEditChange(
                                                                                                                'first_name',
                                                                                                                event.target.value
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="First name"
                                                                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                                                    />
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        value={providerUserEdit?.form.last_name ?? ''}
                                                                                                        onChange={(event) =>
                                                                                                            handleProviderUserEditChange(
                                                                                                                'last_name',
                                                                                                                event.target.value
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Last name"
                                                                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                                                    />
                                                                                                    <input
                                                                                                        type="email"
                                                                                                        value={providerUserEdit?.form.email ?? ''}
                                                                                                        onChange={(event) =>
                                                                                                            handleProviderUserEditChange(
                                                                                                                'email',
                                                                                                                event.target.value
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Email"
                                                                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                                                    />
                                                                                                    <input
                                                                                                        type="text"
                                                                                                        value={providerUserEdit?.form.phone ?? ''}
                                                                                                        onChange={(event) =>
                                                                                                            handleProviderUserEditChange(
                                                                                                                'phone',
                                                                                                                event.target.value
                                                                                                            )
                                                                                                        }
                                                                                                        placeholder="Phone"
                                                                                                        className="w-full rounded-md border border-mld-panel-border px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-mld-primary"
                                                                                                    />
                                                                                                </div>
                                                                                                <div className="mt-3 space-y-2">
                                                                                                    <p className="text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                                                                                        Assign Role
                                                                                                    </p>
                                                                                                    <div className="flex flex-wrap gap-3 text-sm text-mld-text-secondary">
                                                                                                        {(providerRolesByProvider[
                                                                                                            providerUserEdit?.providerId ?? 0
                                                                                                        ] ?? []).map((role) => (
                                                                                                            <label
                                                                                                                key={role.id}
                                                                                                                className="inline-flex items-center gap-2">
                                                                                                                <input
                                                                                                                    type="radio"
                                                                                                                    name={`provider-edit-role-${providerUserEdit?.providerId ?? 0}-${providerUserEdit?.id ?? 0}`}
                                                                                                                    checked={providerUserEdit?.role_id === role.id}
                                                                                                                    onChange={() =>
                                                                                                                        handleProviderEditRoleSelect(role.id)
                                                                                                                    }
                                                                                                                    className="h-4 w-4 rounded border-mld-panel-border text-mld-primary focus:ring-mld-primary"
                                                                                                                />
                                                                                                                <span>
                                                                                                                    {role.name}
                                                                                                                    {role.is_default ? ' (default)' : ''}
                                                                                                                </span>
                                                                                                            </label>
                                                                                                        ))}
                                                                                                        {(providerRolesByProvider[
                                                                                                            providerUserEdit?.providerId ?? 0
                                                                                                        ] ?? []).length === 0 ? (
                                                                                                            <span className="text-xs text-mld-muted-text">
                                                                                                                No provider roles found.
                                                                                                            </span>
                                                                                                        ) : null}
                                                                                                    </div>
                                                                                                </div>
                                                                                                <div className="mt-3 flex flex-wrap items-center gap-3">
                                                                                                    <button
                                                                                                        type="button"
                                                                                                        onClick={handleSaveProviderUserEdit}
                                                                                                        disabled={updateProviderUserMutation.isPending}
                                                                                                        className="inline-flex items-center gap-2 rounded-md bg-mld-primary px-3 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                                                                                        {updateProviderUserMutation.isPending ? (
                                                                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                                                                        ) : null}
                                                                                                        Save Changes
                                                                                                    </button>
                                                                                                    <Button
                                                                                                        variant="secondary"
                                                                                                        size="md"
                                                                                                        onClick={handleCancelProviderUserEdit}>
                                                                                                        Cancel
                                                                                                    </Button>
                                                                                                    {providerUserEditMessage.text ? (
                                                                                                        <span
                                                                                                            className={`text-sm ${
                                                                                                                providerUserEditMessage.type ===
                                                                                                                'success'
                                                                                                                    ? 'text-mld-primary'
                                                                                                                    : 'text-mld-danger'
                                                                                                            }`}>
                                                                                                            {providerUserEditMessage.text}
                                                                                                        </span>
                                                                                                    ) : null}
                                                                                                </div>
                                                                                            </div>
                                                                                        </td>
                                                                                    </tr>
                                                                                ) : null}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    ) : null}
                                                    <div className="border-t border-mld-panel-border bg-mld-menu-background">
                                                        {providerAddOpen[group.providerId] ? (
                                                            <div className="px-4 py-4">
                                                                <div className="mb-3 flex items-center justify-between">
                                                                    <p className="text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                                                        New provider user
                                                                    </p>
                                                                </div>
                                                                <div className="grid grid-cols-1 gap-3 md:grid-cols-5 md:items-center">
                                                                    <input
                                                                        type="text"
                                                                        value={providerUserForms[group.providerId]?.first_name ?? ''}
                                                                        onChange={(event) =>
                                                                            handleProviderUserChange(
                                                                                group.providerId,
                                                                                'first_name',
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        placeholder="First name"
                                                                        className={
                                                                            providerErrors.first_name ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS
                                                                        }
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        value={providerUserForms[group.providerId]?.last_name ?? ''}
                                                                        onChange={(event) =>
                                                                            handleProviderUserChange(
                                                                                group.providerId,
                                                                                'last_name',
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        placeholder="Last name"
                                                                        className={
                                                                            providerErrors.last_name ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS
                                                                        }
                                                                    />
                                                                    <input
                                                                        type="email"
                                                                        value={providerUserForms[group.providerId]?.email ?? ''}
                                                                        onChange={(event) =>
                                                                            handleProviderUserChange(
                                                                                group.providerId,
                                                                                'email',
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        placeholder="Email"
                                                                        className={providerErrors.email ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        value={providerUserForms[group.providerId]?.phone ?? ''}
                                                                        onChange={(event) =>
                                                                            handleProviderUserChange(
                                                                                group.providerId,
                                                                                'phone',
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                        placeholder="Phone"
                                                                        className={providerErrors.phone ? INPUT_ERROR_CLASS : INPUT_DEFAULT_CLASS}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCreateProviderUser(group.providerId)}
                                                                        disabled={createProviderUserMutation.isPending}
                                                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-3 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                                                        {createProviderUserMutation.isPending ? (
                                                                            <Loader2 className="h-4 w-4 animate-spin" />
                                                                        ) : null}
                                                                        Create Provider User
                                                                    </button>
                                                                </div>
                                                                <div className="mt-3 space-y-2">
                                                                    <p className="text-xs font-semibold uppercase tracking-wide text-mld-muted-text">
                                                                        Assign Role
                                                                    </p>
                                                                    <div className="flex flex-wrap gap-3 text-sm text-mld-text-secondary">
                                                                        {(providerRolesByProvider[group.providerId] ?? []).map((role) => (
                                                                            <label key={role.id} className="inline-flex items-center gap-2">
                                                                                <input
                                                                                    type="radio"
                                                                                    name={`provider-role-${group.providerId}`}
                                                                                    checked={
                                                                                        providerUserRoleSelections[group.providerId] ===
                                                                                        role.id
                                                                                    }
                                                                                    onChange={() =>
                                                                                        handleProviderRoleSelect(
                                                                                            group.providerId,
                                                                                            role.id
                                                                                        )
                                                                                    }
                                                                                    className="h-4 w-4 rounded border-mld-panel-border text-mld-primary focus:ring-mld-primary"
                                                                                />
                                                                                <span>
                                                                                    {role.name}
                                                                                    {role.is_default ? ' (default)' : ''}
                                                                                </span>
                                                                            </label>
                                                                        ))}
                                                                        {(providerRolesByProvider[group.providerId] ?? []).length === 0 ? (
                                                                            <span className="text-xs text-mld-muted-text">
                                                                                No provider roles found.
                                                                            </span>
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                                {providerUserMessages[group.providerId]?.text ? (
                                                                    <div
                                                                        className={`mt-3 text-sm ${
                                                                            providerUserMessages[group.providerId]?.type === 'success'
                                                                                ? 'text-mld-primary'
                                                                                : 'text-mld-danger'
                                                                        }`}>
                                                                        {providerUserMessages[group.providerId]?.text}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card.Content>
                </Card>
            )}

            {showActionBar && (showModulesSection || showProvidersSection) && selectedOrg && orgModules && (
                <div className="static mt-6 flex flex-col gap-3 rounded-lg border border-mld-panel-border bg-white px-4 py-3 md:flex-row md:items-center">
                    {showModulesSection ? (
                        <button
                            type="button"
                            onClick={handleDisableAll}
                            disabled={isBusy}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-mld-danger/30 px-4 py-2 text-mld-danger hover:bg-mld-danger/5 disabled:opacity-60">
                            {disableMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                            Disable All Modules
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={handleDisableProviderModules}
                            disabled={isBusy}
                            className="inline-flex items-center justify-center gap-2 rounded-md border border-mld-danger/30 px-4 py-2 text-mld-danger hover:bg-mld-danger/5 disabled:opacity-60">
                            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
                            Disable All Provider Modules
                        </button>
                    )}
                    {isBusy ? <span className="text-sm text-mld-muted-text">Working...</span> : null}
                </div>
            )}

            <Dialog
                open={addProviderOpen}
                onClose={() => {
                    setAddProviderOpen(false);
                    setAddProviderName('');
                    setAddProviderNpi('');
                    setAddProviderMessage({type: '', text: ''});
                }}
                className="relative z-50">
                <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center px-4 py-8">
                        <Dialog.Panel className="w-full max-w-md rounded-2xl border border-mld-panel-border bg-white text-left shadow-xl">
                            <div className="border-b border-mld-panel-border px-6 py-4">
                                <Dialog.Title className="text-lg font-semibold text-mld-text-primary">
                                    Add Provider
                                </Dialog.Title>
                                <p className="text-xs text-mld-muted-text">
                                    NPI is optional — if left blank a synthetic NPI will be generated.
                                </p>
                            </div>
                            <div className="space-y-4 px-6 py-5">
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-mld-text-secondary">Provider Name</label>
                                    <input
                                        type="text"
                                        value={addProviderName}
                                        onChange={(event) => {
                                            setAddProviderName(event.target.value);
                                            setAddProviderMessage({type: '', text: ''});
                                        }}
                                        placeholder="e.g. Main Office"
                                        className={INPUT_DEFAULT_CLASS}
                                    />
                                </div>
                                <div>
                                    <label className="mb-1 block text-xs font-medium text-mld-text-secondary">NPI (optional)</label>
                                    <input
                                        type="text"
                                        value={addProviderNpi}
                                        onChange={(event) => {
                                            setAddProviderNpi(event.target.value);
                                            setAddProviderMessage({type: '', text: ''});
                                        }}
                                        placeholder="10-digit NPI"
                                        className={INPUT_DEFAULT_CLASS}
                                    />
                                </div>
                                {addProviderMessage.text ? (
                                    <div className={`text-sm ${addProviderMessage.type === 'success' ? 'text-mld-primary' : 'text-mld-danger'}`}>
                                        {addProviderMessage.text}
                                    </div>
                                ) : null}
                            </div>
                            <div className="flex items-center justify-end gap-3 border-t border-mld-panel-border px-6 py-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setAddProviderOpen(false);
                                        setAddProviderName('');
                                        setAddProviderNpi('');
                                        setAddProviderMessage({type: '', text: ''});
                                    }}
                                    className="rounded-md border border-mld-panel-border px-3 py-2 text-sm text-mld-text-secondary hover:bg-mld-menu-background">
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleAddProvider}
                                    disabled={addProviderMutation.isPending}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-mld-primary px-4 py-2 text-sm text-white hover:bg-mld-primary-700 disabled:opacity-60">
                                    {addProviderMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : null}
                                    Add Provider
                                </button>
                            </div>
                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default OrganizationProviderConsole;
